import { useEffect, useState, type CSSProperties, type ReactElement, type ReactNode } from 'react'
import { motion } from 'framer-motion'
import {
  ANIMAL_INFO,
  GLYPHS,
  Petal,
  Sparkle,
  Cloud,
  HeroCrane,
  ELEMENT_INFO,
  ElementGlyph,
  HeartStatImg,
  StrengthStatImg,
  TieredCritter,
  UnitGlyph,
} from '../design-system'
import type { Animal, Element, GlyphKey } from '@origami/shared'
import {
  EvolutionSequence,
  type EvolutionUnit,
} from '../game/EvolutionSequence'
import { SakuraSeal } from '../game/Shop'
import { EmaPlaque } from '../ui/SoloPlayScreen'
import '../ui/SoloPlayScreen.css'

/**
 * Design Gallery — visual showcase of the design system primitives.
 *
 * Activated via the ?design URL param. Use this to verify the
 * design-system primitives are loading correctly, sanity-check
 * proportions, and reference visual treatments while building real
 * pages.
 *
 * Add new sections as new primitives ship. Don't put production UI
 * here — the gallery is for the foundation, not the game.
 */
export function DesignGallery(): ReactElement {
  return (
    <div className="paper" style={{ minHeight: '100vh', padding: '40px 60px' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <Header />
        <Section title="① Palette">
          <Palette />
        </Section>
        <Section title="①b Wagara (和柄) — Japanese repeat patterns">
          <WagaraGallery />
        </Section>
        <Section title="② Type pairing">
          <Typography />
        </Section>
        <Section title="②b Display font candidates (shop card A/B)">
          <DisplayFontBakeOff />
        </Section>
        <Section title="③ Buttons">
          <Buttons />
        </Section>
        <Section title="④ Cards & chips">
          <CardsAndChips />
        </Section>
        <Section title="④b Icon-disc stat chip (archived shop variant)">
          <IconDiscStatChips />
        </Section>
        <Section title="⑤ Glyphs (origami silhouettes)">
          <GlyphGrid />
        </Section>
        <Section title="⑤b UnitGlyph (data → render bridge)">
          <UnitGlyphRow />
        </Section>
        <Section title="⑥ Decorations">
          <Decorations />
        </Section>
        <Section title="⑦ Element badges & matchup">
          <Elements />
        </Section>
        <Section title="⑧ Stat indicators">
          <BitmapStats />
        </Section>
        <Section title="⑨ Winner stamp (hanko)">
          <WinnerStamps />
        </Section>
        <Section title="⑨b Wax-seal sakura stamp (retired shop avatar)">
          <SakuraSealShowcase />
        </Section>
        <Section title="⑨c Player identity ema (絵馬 hanging plaque)">
          <PlayerEmaShowcase />
        </Section>
        <Section title="⑨d Saisenbako 賽銭箱 (archived sell-zone concept)">
          <SaisenbakoShowcase />
        </Section>
        <Section title="⑩ Card vocabulary">
          <CardVocabulary />
        </Section>
        <Section title="⑩b Forged-frame card (stat glyphs baked into chrome)">
          <ForgedFrameCards />
        </Section>
        <Section title="⑪ HeroCrane">
          <HeroCraneRow />
        </Section>
        <Section title="⑫ Animation showcase">
          <AnimationShowcase />
        </Section>
        <Section title="⑬ Evolution sequence (combine animation)">
          <EvolutionDemo />
        </Section>
      </div>
    </div>
  )
}

function Header(): ReactElement {
  return (
    <div style={{ marginBottom: 48 }}>
      <div className="tiny-cap" style={{ color: 'var(--ink-fade)', marginBottom: 6 }}>
        ✦ DESIGN SYSTEM GALLERY ✦
      </div>
      <h1 className="cute-title" style={{ fontSize: 64, marginBottom: 12 }}>
        Origami Autobattler
      </h1>
      <p className="hand-note" style={{ fontSize: 16, maxWidth: 720 }}>
        Foundation primitives ported from the Claude Design handoff. These
        components are the building blocks for production pages — palette,
        typography, glyphs, decorations. Production pages will compose them
        into real game UI wired to backend state.
      </p>
      <p className="hand-note" style={{ fontSize: 14, marginTop: 8 }}>
        <a href="/" style={{ color: 'var(--sakura-deep)' }}>
          ← back to health check
        </a>
      </p>
    </div>
  )
}

function Section({
  title,
  children,
}: {
  title: string
  children: ReactNode
}): ReactElement {
  return (
    <div style={{ marginBottom: 56 }}>
      <h2
        className="cute-title"
        style={{
          fontSize: 28,
          marginBottom: 16,
          paddingBottom: 8,
          borderBottom: '2px dashed var(--ink-fade)',
        }}
      >
        {title}
      </h2>
      {children}
    </div>
  )
}

function Swatch({ color, name }: { color: string; name: string }): ReactElement {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div
        style={{
          width: 90,
          height: 60,
          background: color,
          border: '2px solid var(--ink)',
          borderRadius: 12,
        }}
      />
      <span className="tiny-cap" style={{ color: 'var(--ink-fade)' }}>
        {name}
      </span>
      <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--ink-fade)' }}>
        {color}
      </span>
    </div>
  )
}

function Palette(): ReactElement {
  const accents: Array<[string, string]> = [
    ['var(--washi)', 'washi'],
    ['var(--washi-light)', 'washi-light'],
    ['var(--cream)', 'cream'],
    ['var(--sakura)', 'sakura'],
    ['var(--mint)', 'mint'],
    ['var(--sky)', 'sky'],
    ['var(--lavender)', 'lavender'],
    ['var(--peach)', 'peach'],
  ]
  const elements: Array<[string, string]> = [
    ['var(--el-fire)', 'el-fire'],
    ['var(--el-water)', 'el-water'],
    ['var(--el-wind)', 'el-wind'],
    ['var(--el-wood)', 'el-wood'],
    ['var(--el-earth)', 'el-earth'],
  ]
  // Chiyogami pairs — each row shows the deep dye stroke color and
  // its matching pale-bg color used together in the wagara surfaces.
  const chiyogamiPairs: Array<{ ink: string; bg: string; label: string }> = [
    { ink: '--chiyo-indigo',     bg: '--chiyo-indigo-bg',     label: 'indigo (ai)' },
    { ink: '--chiyo-vermillion', bg: '--chiyo-vermillion-bg', label: 'vermillion (aka)' },
    { ink: '--chiyo-moss',       bg: '--chiyo-moss-bg',       label: 'moss' },
    { ink: '--chiyo-plum',       bg: '--chiyo-plum-bg',       label: 'plum (ume)' },
    { ink: '--chiyo-kincha',     bg: '--chiyo-kincha-bg',     label: 'kincha (deep gold)' },
  ]
  return (
    <div style={{ display: 'grid', gap: 24 }}>
      <div>
        <div className="tiny-cap" style={{ color: 'var(--ink-fade)', marginBottom: 10 }}>
          BASE & ACCENTS
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {accents.map(([c, n]) => (
            <Swatch key={n} color={c} name={n} />
          ))}
        </div>
      </div>
      <div>
        <div className="tiny-cap" style={{ color: 'var(--ink-fade)', marginBottom: 10 }}>
          ELEMENTS
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {elements.map(([c, n]) => (
            <Swatch key={n} color={c} name={n} />
          ))}
        </div>
      </div>
      <div>
        <div className="tiny-cap" style={{ color: 'var(--ink-fade)', marginBottom: 10 }}>
          CHIYOGAMI — DYE PAIRS (stroke + bg)
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {chiyogamiPairs.map(({ ink, bg, label }) => (
            <ChiyoPair key={ink} ink={ink} bg={bg} label={label} />
          ))}
        </div>
      </div>
    </div>
  )
}

function ChiyoPair({
  ink,
  bg,
  label,
}: {
  ink: string
  bg: string
  label: string
}): ReactElement {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div
        style={{
          width: 130,
          height: 60,
          background: `rgb(var(${bg}))`,
          border: '2px solid var(--ink)',
          borderRadius: 12,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: `rgb(var(${ink}))`,
            clipPath: 'polygon(0 0, 50% 0, 50% 100%, 0 100%)',
          }}
        />
      </div>
      <span className="tiny-cap" style={{ color: 'var(--ink-fade)' }}>
        {label}
      </span>
      <span style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--ink-fade)' }}>
        {ink} / {bg}
      </span>
    </div>
  )
}

/** Wagara (和柄) — Japanese repeat patterns surfaced as CSS custom
 *  properties in styles/wagara.css. Each tile below pulls directly
 *  from those variables so this gallery stays in sync with the
 *  shipping library — if you swap a pattern's geometry, this view
 *  updates automatically.
 */
interface WagaraSpec {
  /** CSS variable name for the colored variant rendered as the hero tile. */
  varName: string
  /** Alternate variant names also exposed by the library, listed for reference. */
  alts: string[]
  kanji: string
  romaji: string
  meaning: string
  /** Background-size for the swatch panel — must match the SVG tile dimensions. */
  tile: [number, number]
  /** Background-color showing through the pattern. */
  bgVar: string
  /** Where the pattern is used in production (so this gallery doubles as a usage index). */
  usedIn?: string
}

const WAGARA_SPECS: WagaraSpec[] = [
  {
    varName: '--wagara-seigaiha-indigo',
    alts: ['--wagara-seigaiha-ink'],
    kanji: '青海波',
    romaji: 'seigaiha',
    meaning: 'blue sea wave',
    tile: [60, 60],
    bgVar: '--chiyo-indigo-bg',
    usedIn: 'Shop · lineup rack',
  },
  {
    varName: '--wagara-asanoha-vermillion',
    alts: ['--wagara-asanoha-ink'],
    kanji: '麻の葉',
    romaji: 'asanoha',
    meaning: 'hemp leaf',
    tile: [60, 52],
    bgVar: '--chiyo-vermillion-bg',
  },
  {
    varName: '--wagara-shippo-moss',
    alts: ['--wagara-shippo-ink'],
    kanji: '七宝',
    romaji: 'shippō',
    meaning: 'seven treasures',
    tile: [56, 56],
    bgVar: '--chiyo-moss-bg',
    usedIn: 'Shop · bench rack',
  },
  {
    varName: '--wagara-kikko-kincha',
    alts: [],
    kanji: '亀甲',
    romaji: 'kikkō',
    meaning: 'tortoise shell',
    tile: [60, 52],
    bgVar: '--chiyo-kincha-bg',
    usedIn: 'Shop · header',
  },
  {
    varName: '--wagara-samekomon-ink',
    alts: [],
    kanji: '鮫小紋',
    romaji: 'samekomon',
    meaning: 'shark skin',
    tile: [40, 40],
    bgVar: '--washi-light',
    usedIn: 'Shop · synergy rail',
  },
  {
    varName: '--wagara-tatewaku-plum',
    alts: [],
    kanji: '立涌',
    romaji: 'tatewaku',
    meaning: 'rising steam',
    tile: [30, 120],
    bgVar: '--chiyo-plum-bg',
  },
]

function WagaraGallery(): ReactElement {
  return (
    <div>
      <p className="hand-note" style={{ marginTop: 0, marginBottom: 16, maxWidth: 760 }}>
        Hand-drawn SVG repeat tiles based on classic Edo-period{' '}
        <i>wagara</i> (和柄). Each pattern ships as a CSS custom property
        (<code>var(--wagara-…)</code>) plus a utility class
        (<code>.wagara-…</code>) defined in{' '}
        <code>styles/wagara.css</code>. Reach for these whenever a
        surface wants real Japanese textile character instead of a
        flat tint.
      </p>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
          gap: 18,
          marginBottom: 28,
        }}
      >
        {WAGARA_SPECS.map((spec) => (
          <WagaraTile key={spec.varName} spec={spec} />
        ))}
      </div>
      <WagaraRackDemo />
    </div>
  )
}

function WagaraTile({ spec }: { spec: WagaraSpec }): ReactElement {
  return (
    <div
      className="cute-card"
      style={{
        padding: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          height: 140,
          backgroundColor: `rgb(var(${spec.bgVar}))`,
          backgroundImage: `var(${spec.varName})`,
          backgroundSize: `${spec.tile[0]}px ${spec.tile[1]}px`,
          backgroundRepeat: 'repeat',
          borderBottom: '2px solid var(--ink)',
        }}
        aria-label={`${spec.romaji} pattern preview`}
      />
      <div style={{ padding: '10px 14px 12px', display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span
            style={{
              fontFamily: 'var(--serif)',
              fontSize: 20,
              color: 'var(--ink)',
              lineHeight: 1,
            }}
          >
            {spec.kanji}
          </span>
          <span
            className="cute-title"
            style={{ fontSize: 16, lineHeight: 1 }}
          >
            {spec.romaji}
          </span>
        </div>
        <span className="hand-note" style={{ fontSize: 12, marginTop: 1 }}>
          “{spec.meaning}”
        </span>
        <code
          style={{
            fontFamily: 'var(--mono)',
            fontSize: 10,
            color: 'var(--ink-fade)',
            marginTop: 6,
            wordBreak: 'break-all',
          }}
        >
          {spec.varName}
        </code>
        {spec.alts.length > 0 && (
          <span
            className="tiny-cap"
            style={{ color: 'var(--ink-fade)', fontSize: 8, marginTop: 2 }}
          >
            ALT: {spec.alts.map((a) => a.replace('--wagara-', '')).join(' · ')}
          </span>
        )}
        {spec.usedIn && (
          <span
            className="chip"
            style={{
              alignSelf: 'flex-start',
              marginTop: 6,
              fontSize: 9,
              padding: '1px 7px',
              background: 'var(--washi-light)',
            }}
          >
            {spec.usedIn}
          </span>
        )}
      </div>
    </div>
  )
}

/** Side-by-side preview of how the patterns combine into the shop's
 *  layered-chiyogami rack vocabulary. Static visual reference — these
 *  do not host real cards; they're just the tinted-pattern containers
 *  the shop uses around its slot rows.
 */
function WagaraRackDemo(): ReactElement {
  return (
    <div>
      <div className="tiny-cap" style={{ color: 'var(--ink-fade)', marginBottom: 10 }}>
        COMPOSITION — SHOP RACK VOCABULARY
      </div>
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
        <DemoRack
          label="LINEUP ✦"
          kanji="青海波"
          background={{
            color: 'rgba(216, 230, 240, 0.75)',
            image: 'var(--wagara-seigaiha-indigo)',
            size: '60px 60px',
            border: 'rgba(38, 78, 124, 0.55)',
          }}
        />
        <DemoRack
          label="BENCH 💤"
          kanji="七宝"
          background={{
            color: 'rgba(220, 232, 196, 0.78)',
            image: 'var(--wagara-shippo-moss)',
            size: '56px 56px',
            border: 'rgba(96, 132, 76, 0.55)',
          }}
        />
      </div>
    </div>
  )
}

function DemoRack({
  label,
  kanji,
  background,
}: {
  label: string
  kanji: string
  background: { color: string; image: string; size: string; border: string }
}): ReactElement {
  return (
    <div
      style={{
        position: 'relative',
        minWidth: 280,
        padding: '28px 36px',
        border: `2px solid ${background.border}`,
        borderRadius: 18,
        backgroundColor: background.color,
        backgroundImage: background.image,
        backgroundSize: background.size,
        backgroundRepeat: 'repeat',
        backgroundBlendMode: 'multiply',
        boxShadow: 'inset 0 0 0 1px rgba(255, 251, 237, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: -10,
          left: 14,
          padding: '2px 10px',
          background: 'var(--washi-light)',
          border: '2px solid var(--ink)',
          borderRadius: 999,
          fontFamily: 'var(--mono)',
          fontSize: 9,
          letterSpacing: '0.18em',
          fontWeight: 700,
          color: 'var(--ink)',
          boxShadow: '0 2px 0 var(--ink)',
        }}
      >
        {label}
      </span>
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          style={{
            width: 60,
            height: 80,
            border: '2px dashed var(--ink-fade)',
            borderRadius: 10,
            background: 'rgba(255, 255, 255, 0.4)',
          }}
        />
      ))}
      <span
        style={{
          position: 'absolute',
          bottom: 6,
          right: 12,
          fontFamily: 'var(--serif)',
          fontSize: 22,
          color: 'rgba(58, 42, 24, 0.35)',
        }}
      >
        {kanji}
      </span>
    </div>
  )
}

function Typography(): ReactElement {
  return (
    <div className="cute-card" style={{ padding: 24, display: 'grid', gap: 12 }}>
      <div style={{ fontFamily: 'var(--serif)', fontSize: 36 }}>
        RocknRoll One — display serif (current --serif)
      </div>
      <div style={{ fontFamily: "'Caprasimo', serif", fontSize: 32, color: 'var(--ink-fade)' }}>
        Caprasimo — former display serif (archived, see §②b)
      </div>
      <div style={{ fontFamily: 'var(--hand)', fontSize: 18 }}>
        Klee One — handwritten notes &amp; flavor text
      </div>
      <div style={{ fontFamily: 'var(--sans)', fontSize: 16 }}>
        Quicksand — body text &amp; UI labels (Regular / Medium / Semibold / Bold)
      </div>
      <div className="tiny-cap">tiny mono caps · jetbrains mono</div>
      <div
        style={{
          fontFamily: 'var(--pixel)',
          fontSize: 40,
          letterSpacing: 1,
          lineHeight: 1,
        }}
      >
        Jersey 15 — 0 1 2 3 4 5 6 7 8 9 · pixel numerals for stat readouts
      </div>
    </div>
  )
}

/* ------------------------------------------------------------
 *  Display font bake-off — A/B alternates to Caprasimo, each
 *  rendered inside a real shop-card so the typography is
 *  judged in the context where it actually lives (kanji chip
 *  + element tint + bitmap stats + tier hanko around it,
 *  competing for the eye). Caprasimo is included as the
 *  baseline row; promote a winning candidate by repointing
 *  `--serif` in tokens.css.
 * ------------------------------------------------------------ */

const FONT_CANDIDATES: Array<{
  cssVar: string
  label: string
  blurb: string
  /** Optional inline letterSpacing tuning — some display fonts
   *  read tight at large sizes. */
  letterSpacing?: number
}> = [
  {
    cssVar: 'var(--display-rocknroll)',
    label: 'RocknRoll One (current)',
    blurb: 'Bilingual chunky display — Latin + kanji designed together. The current --serif winner.',
  },
  {
    cssVar: "'Caprasimo', serif",
    label: 'Caprasimo (former)',
    blurb: 'Chunky western display serif. Friendly, geometric, Latin-only. The previous --serif baseline.',
  },
  {
    cssVar: 'var(--display-reggae)',
    label: 'Reggae One',
    blurb: 'Bolder poster-blocky bilingual face. Maximum stage presence.',
  },
  {
    cssVar: 'var(--display-fraunces)',
    label: 'Fraunces (700)',
    blurb: 'Refined Latin display serif with optical contrast. Literary register.',
    letterSpacing: -0.5,
  },
  {
    cssVar: 'var(--display-mincho)',
    label: 'Hina Mincho',
    blurb: 'Traditional Japanese mincho — calligraphic, tea-house refined.',
  },
]

const FONT_SAMPLE_UNITS: Array<{
  name: string
  glyph: GlyphKey
  element: Element
  animal: 'mammal' | 'reptile' | 'bird' | 'insect' | 'fish'
  tier: 1 | 2 | 3 | 4 | 5
  hp: number
  atk: number
}> = [
  { name: 'Ash Wolf',      glyph: 'wolf',          element: 'fire',  animal: 'mammal',  tier: 4, hp: 9, atk: 5 },
  { name: 'Marsh Caiman',  glyph: 'caiman',        element: 'water', animal: 'reptile', tier: 2, hp: 3, atk: 4 },
  { name: 'Storm Hawk',    glyph: 'hawk',          element: 'wind',  animal: 'bird',    tier: 4, hp: 6, atk: 5 },
  { name: 'Walking Stick', glyph: 'walking_stick', element: 'wood',  animal: 'insect',  tier: 2, hp: 2, atk: 3 },
]

const ELE_SOFT_BG: Record<Element, string> = {
  fire:  '#ffe2d6',
  water: '#dde9f6',
  wind:  '#f0e8b8',
  wood:  '#e0ecca',
  earth: '#d4b88a',
}

const ANIMAL_KANJI = { mammal: '獣', reptile: '蛇', bird: '鳥', insect: '虫', fish: '魚' } as const
const ANIMAL_BG = {
  mammal:  'var(--peach)',
  reptile: 'var(--sky)',
  bird:    'var(--lavender)',
  insect:  'var(--mint)',
  fish:    'var(--sakura)',
} as const

const TIER_ROMAN_LOCAL: Record<number, string> = { 1: 'I', 2: 'II', 3: 'III', 4: 'IV', 5: 'V' }

function DisplayFontBakeOff(): ReactElement {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
      <p className="hand-note" style={{ margin: 0, maxWidth: 720 }}>
        Each row pins one display-font candidate against the same four
        sample units (fire mammal · water reptile · wind bird · wood
        insect). The font is applied only to the unit-name plate so the
        rest of the card vocabulary (kanji chip, animal hanko, tier
        seal, bitmap stats) stays constant — every difference you see
        belongs to the typeface. To promote a winner, repoint{' '}
        <code style={{ fontFamily: 'var(--mono)' }}>--serif</code> in{' '}
        <code style={{ fontFamily: 'var(--mono)' }}>tokens.css</code>.
      </p>
      {FONT_CANDIDATES.map((font) => (
        <FontRow key={font.label} font={font} />
      ))}
    </div>
  )
}

function FontRow({
  font,
}: {
  font: (typeof FONT_CANDIDATES)[number]
}): ReactElement {
  return (
    <div
      className="cute-card"
      style={{
        padding: 20,
        display: 'grid',
        gridTemplateColumns: '220px 1fr',
        gap: 24,
        alignItems: 'stretch',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, borderRight: '1.5px dashed var(--ink-fade)', paddingRight: 16 }}>
        <div
          style={{
            fontFamily: font.cssVar,
            fontSize: 28,
            lineHeight: 1.1,
            color: 'var(--ink)',
            letterSpacing: font.letterSpacing,
          }}
        >
          {font.label}
        </div>
        <div
          style={{
            fontFamily: font.cssVar,
            fontSize: 20,
            lineHeight: 1.2,
            color: 'var(--ink)',
            letterSpacing: font.letterSpacing,
          }}
        >
          Origami Odyssey
        </div>
        <div
          style={{
            fontFamily: font.cssVar,
            fontSize: 18,
            lineHeight: 1.3,
            color: 'var(--ink-fade)',
          }}
        >
          折り紙の旅
        </div>
        <p
          className="hand-note"
          style={{ margin: '6px 0 0', fontSize: 12, lineHeight: 1.4, color: 'var(--ink-fade)' }}
        >
          {font.blurb}
        </p>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
          gap: 12,
        }}
      >
        {FONT_SAMPLE_UNITS.map((unit) => (
          <FontCardSwatch key={unit.name} unit={unit} fontVar={font.cssVar} letterSpacing={font.letterSpacing} />
        ))}
      </div>
    </div>
  )
}

function FontCardSwatch({
  unit,
  fontVar,
  letterSpacing,
}: {
  unit: (typeof FONT_SAMPLE_UNITS)[number]
  fontVar: string
  letterSpacing?: number
}): ReactElement {
  const elInfo = ELEMENT_INFO[unit.element]
  return (
    <article
      className="cute-card"
      style={{
        position: 'relative',
        padding: '14px 12px 14px',
        background: ELE_SOFT_BG[unit.element],
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        minHeight: 240,
      }}
    >
      {/* element kanji chip — top-left, overhanging */}
      <span
        aria-hidden
        style={{
          position: 'absolute',
          top: -10,
          left: 8,
          width: 28,
          height: 28,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          background: elInfo.bg,
          color: elInfo.fg,
          fontFamily: 'var(--sans)',
          fontWeight: 700,
          fontSize: 13,
          border: '2px solid var(--ink)',
          filter: 'drop-shadow(0 1px 0 var(--ink))',
        }}
      >
        {elInfo.kanji}
      </span>
      {/* animal hanko — top-right, square seal */}
      <span
        aria-hidden
        style={{
          position: 'absolute',
          top: -10,
          right: 8,
          width: 28,
          height: 28,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: ANIMAL_BG[unit.animal as keyof typeof ANIMAL_BG],
          color: 'var(--ink)',
          fontFamily: 'var(--sans)',
          fontWeight: 700,
          fontSize: 13,
          border: '2px solid var(--ink)',
          borderRadius: 4,
          filter: 'drop-shadow(0 1px 0 var(--ink))',
        }}
      >
        {ANIMAL_KANJI[unit.animal as keyof typeof ANIMAL_KANJI]}
      </span>
      {/* tier hanko — bottom-right, vermillion seal */}
      <span
        aria-hidden
        style={{
          position: 'absolute',
          bottom: -8,
          right: 8,
          width: 30,
          height: 30,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 8,
          background: 'rgb(var(--chiyo-vermillion-bg))',
          color: 'rgb(var(--chiyo-vermillion))',
          border: '2.5px solid currentColor',
          fontFamily: 'var(--mono)',
          fontWeight: 700,
          fontSize: 12,
          letterSpacing: 0.5,
          transform: 'rotate(-3deg)',
          boxShadow: '0 0 0 2px var(--washi-light) inset',
        }}
      >
        {TIER_ROMAN_LOCAL[unit.tier]}
      </span>
      {/* art */}
      <div style={{ marginTop: 8 }}>
        <TieredCritter glyph={unit.glyph} star={1} size={78} />
      </div>
      {/* the swap point — display font lives here */}
      <h3
        style={{
          margin: '4px 0 0',
          fontFamily: fontVar,
          fontSize: 17,
          fontWeight: 700,
          lineHeight: 1.05,
          letterSpacing,
          color: 'var(--ink)',
          textAlign: 'center',
        }}
      >
        {unit.name}
      </h3>
      {/* type strip */}
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '2px 6px',
          background: 'var(--washi-light)',
          border: '1px solid var(--ink-fade)',
          borderRadius: 3,
          fontFamily: 'var(--mono)',
          fontSize: 8,
          letterSpacing: 1.3,
          color: 'var(--ink)',
          textTransform: 'uppercase',
        }}
      >
        <span>{elInfo.kanji} {elInfo.en}</span>
        <span style={{ opacity: 0.4 }}>·</span>
        <span>{ANIMAL_KANJI[unit.animal as keyof typeof ANIMAL_KANJI]} {unit.animal}</span>
      </div>
      {/* stat row */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 'auto' }}>
        <HeartStatImg value={unit.hp} size={34} />
        <StrengthStatImg value={unit.atk} size={34} />
      </div>
    </article>
  )
}

function Buttons(): ReactElement {
  const variants = ['', 'mint', 'sky', 'gold', 'peach', 'lav', 'cream', 'dark']
  return (
    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
      {variants.map((v) => (
        <button key={v || 'default'} className={`cute-btn ${v}`}>
          {v ? `.${v}` : 'default'}
        </button>
      ))}
    </div>
  )
}

function CardsAndChips(): ReactElement {
  return (
    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
      <div className="cute-card" style={{ padding: '20px 24px', maxWidth: 320 }}>
        <div className="tiny-cap" style={{ color: 'var(--ink-fade)', marginBottom: 6 }}>
          .cute-card
        </div>
        <div className="cute-title" style={{ fontSize: 22, marginBottom: 8 }}>
          Pillowy paper card
        </div>
        <p className="hand-note" style={{ margin: 0 }}>
          The base container for every panel — washi-light fill, ink border, chunky drop-shadow.
        </p>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-start' }}>
        <span className="chip">.chip</span>
        <span className="chip bg-fire">FIRE</span>
        <span className="chip bg-water">WATER</span>
        <span className="chip bg-wind">WIND</span>
        <span className="chip bg-wood">WOOD</span>
        <span className="chip bg-earth">EARTH</span>
      </div>
    </div>
  )
}

/** Archived shop stat-chip variant — colored tile with a square
 *  icon-disc and a pixel numeral. The production shop banner moved
 *  to a hanko-stamp row (rice paper + colored kanji glyph) in May
 *  2026; this component preserves the prior design vocabulary in
 *  case a future surface wants the bolder colored-tile treatment.
 *  All styles are inline so this stays a frozen reference even if
 *  the .shop-stat rules continue to evolve. */
function IconDiscStatChips(): ReactElement {
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
      <IconDiscChip icon="◈" label="GOLD" value="05" bg="var(--gold)" />
      <IconDiscChip icon="♥" label="HEALTH" value="22" sub="/30" bg="var(--sakura)" />
      <IconDiscChip icon="◉" label="WINS" value="04" sub="/10" bg="var(--mint)" />
    </div>
  )
}

interface IconDiscChipProps {
  icon: string
  label: string
  value: string
  sub?: string
  bg: string
}

function IconDiscChip({ icon, label, value, sub, bg }: IconDiscChipProps): ReactElement {
  const pixelShadow =
    '1.5px 0 0 #1a0e10, -1.5px 0 0 #1a0e10, 0 1.5px 0 #1a0e10, 0 -1.5px 0 #1a0e10, 1.5px 1.5px 0 #1a0e10, -1.5px 1.5px 0 #1a0e10, -1.5px -1.5px 0 #1a0e10'
  const subShadow =
    '1px 0 0 #1a0e10, -1px 0 0 #1a0e10, 0 1px 0 #1a0e10, 0 -1px 0 #1a0e10, 1px 1px 0 #1a0e10, -1px 1px 0 #1a0e10'
  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        padding: '6px 14px 6px 6px',
        background: bg,
        border: '2.5px solid var(--ink)',
        borderRadius: 16,
        boxShadow: '0 4px 0 var(--ink)',
      }}
    >
      {/* inner rice ring — the inside of a stamped page */}
      <span
        aria-hidden
        style={{
          position: 'absolute',
          inset: 2,
          borderRadius: 13,
          pointerEvents: 'none',
          boxShadow: 'inset 0 0 0 1px rgba(255, 251, 237, 0.45)',
        }}
      />
      <span
        aria-hidden
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          background: 'var(--rice)',
          border: '2px solid var(--ink)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'var(--serif)',
          fontSize: 18,
          color: 'var(--ink)',
          flexShrink: 0,
        }}
      >
        {icon}
      </span>
      <span
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          lineHeight: 1,
          gap: 3,
        }}
      >
        <span
          style={{
            fontFamily: 'var(--mono)',
            fontSize: 9,
            letterSpacing: '0.14em',
            fontWeight: 700,
            color: 'var(--ink)',
          }}
        >
          {label}
        </span>
        <span
          style={{
            fontFamily: 'var(--pixel)',
            fontSize: 26,
            color: '#fffaee',
            textShadow: pixelShadow,
            lineHeight: 1,
            display: 'inline-flex',
            alignItems: 'baseline',
          }}
        >
          {value}
          {sub && (
            <span
              style={{
                fontFamily: 'var(--pixel)',
                fontSize: 15,
                color: '#fffaee',
                textShadow: subShadow,
                marginLeft: 3,
                opacity: 0.82,
              }}
            >
              {sub}
            </span>
          )}
        </span>
      </span>
    </div>
  )
}

function GlyphGrid(): ReactElement {
  const keys = Object.keys(GLYPHS)
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
        gap: 16,
      }}
    >
      {keys.map((key) => {
        const Glyph = GLYPHS[key]!
        return (
          <div
            key={key}
            className="cute-card"
            style={{
              padding: 16,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <div className="bob">
              <Glyph size={100} />
            </div>
            <span className="tiny-cap" style={{ color: 'var(--ink-fade)' }}>
              {key}
            </span>
          </div>
        )
      })}
    </div>
  )
}

function UnitGlyphRow(): ReactElement {
  const mockUnits = [
    { id: 'crane_flock',    name: 'Crane Flock',  glyph: 'crane' },
    { id: 'paper_mite',     name: 'Paper Mite',   glyph: 'beetle' },
    { id: 'shadow_fox',     name: 'Shadow Fox',   glyph: 'fox' },
    { id: 'ember_butterfly',name: 'Ember Moth',   glyph: 'butterfly' },
  ]
  return (
    <div>
      <p className="hand-note" style={{ marginTop: 0, marginBottom: 16, maxWidth: 720 }}>
        Each card below is a mock unit object — the kind of data the server
        sends to the client. <code>UnitGlyph</code> looks up{' '}
        <code>unit.glyph</code> in the GLYPHS map and renders the matching
        silhouette. This is the data → render bridge that production
        components use.
      </p>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
          gap: 16,
        }}
      >
        {mockUnits.map((unit) => (
          <div
            key={unit.id}
            className="cute-card"
            style={{
              padding: 16,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <div className="bob">
              <UnitGlyph unit={unit} size={100} />
            </div>
            <span className="cute-title" style={{ fontSize: 16 }}>
              {unit.name}
            </span>
            <span className="tiny-cap" style={{ color: 'var(--ink-fade)' }}>
              glyph: {unit.glyph}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

function Decorations(): ReactElement {
  return (
    <div style={{ display: 'flex', gap: 32, alignItems: 'center', flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
        <div className="petal" style={{ position: 'relative' }}>
          <Petal size={40} />
        </div>
        <span className="tiny-cap" style={{ color: 'var(--ink-fade)' }}>Petal</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
        <div className="sparkle">
          <Sparkle size={40} />
        </div>
        <span className="tiny-cap" style={{ color: 'var(--ink-fade)' }}>Sparkle</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
        <Cloud size={140} />
        <span className="tiny-cap" style={{ color: 'var(--ink-fade)' }}>Cloud</span>
      </div>
    </div>
  )
}

function Elements(): ReactElement {
  const elements: Element[] = ['fire', 'water', 'wind', 'wood', 'earth']
  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
      {elements.map((el) => {
        const info = ELEMENT_INFO[el]
        return (
          <div
            key={el}
            style={{
              padding: '10px 16px',
              background: info.bg,
              color: info.fg,
              border: '2px solid var(--ink)',
              borderRadius: 14,
              boxShadow: '0 3px 0 var(--ink)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontFamily: 'var(--serif)',
              fontSize: 18,
            }}
          >
            <ElementGlyph element={el} size={20} />
            <span>{info.en}</span>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 11, opacity: 0.7 }}>
              ({info.kanji})
            </span>
          </div>
        )
      })}
    </div>
  )
}

function BitmapStats(): ReactElement {
  const mockUnits = [
    { id: 'shadow_crane', name: 'Shadow Crane', glyph: 'flock', element: 'wind'  as const, hp: 8,  atk: 4 },
    { id: 'paper_mite',   name: 'Paper Mite',   glyph: 'mite',  element: 'earth' as const, hp: 14, atk: 7 },
    { id: 'tinder_fox',   name: 'Tinder Fox',   glyph: 'fox',   element: 'fire'  as const, hp: 22, atk: 12 },
  ]
  return (
    <div>
      <p className="hand-note" style={{ marginTop: 0, marginBottom: 16, maxWidth: 720 }}>
        Bitmap stat indicators authored from the origami-paper icons —
        the only stat indicators in the production roster (shop /
        encyclopedia / battle screen). The unit-card grid below mounts
        them against a real card backdrop to sanity-check contrast and
        proportion across the Jersey-15 pixel numerals.
      </p>
      <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 24 }}>
        <HeartStatImg value={5} size={48} />
        <HeartStatImg value={12} size={48} />
        <HeartStatImg value={99} size={48} />
        <div style={{ width: 24 }} />
        <StrengthStatImg value={3} size={48} />
        <StrengthStatImg value={7} size={48} />
        <StrengthStatImg value={14} size={48} />
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
          gap: 16,
        }}
      >
        {mockUnits.map((unit) => {
          const info = ELEMENT_INFO[unit.element]
          return (
            <div
              key={unit.id}
              className="cute-card"
              style={{
                padding: 16,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <span
                className="chip"
                style={{
                  background: info.bg,
                  color: info.fg,
                  alignSelf: 'flex-start',
                }}
              >
                {info.en}
              </span>
              <div className="bob">
                <UnitGlyph unit={unit} size={100} />
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <HeartStatImg value={unit.hp} size={48} />
                <StrengthStatImg value={unit.atk} size={48} />
              </div>
              <span className="cute-title" style={{ fontSize: 16 }}>
                {unit.name}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------
 *  Winner stamp (hanko) — the triumph marker that drops onto
 *  the paper-theater stage at battle end. Source of truth:
 *  `WinnerBanner` in ui/BattleScreen.tsx. Reproduced here as a
 *  static showcase (no animation reset on rerender). Each
 *  variant overrides the production absolute positioning so the
 *  three plates sit side-by-side in normal flow.
 * ------------------------------------------------------------ */

function WinnerStamps(): ReactElement {
  const variants: Array<{
    key: 'A' | 'B' | 'tie'
    kanji: string
    caption: string
    team: string
    label: string
  }> = [
    { key: 'A',   kanji: '勝', caption: 'winner', team: 'team A · 折',  label: 'Team A wins (indigo)' },
    { key: 'B',   kanji: '勝', caption: 'winner', team: 'team B · 折',  label: 'Team B wins (vermillion)' },
    { key: 'tie', kanji: '引', caption: 'a tie',  team: '— · 引き分け', label: 'Stalemate (washi)' },
  ]
  return (
    <div>
      <p className="hand-note" style={{ marginTop: 0, marginBottom: 24, maxWidth: 720 }}>
        Hanko-stamp triumph marker. Drops onto the paper-theater
        stage at battle end on the winning team's half (or center
        for a tie). Source of truth lives in
        <code style={{ margin: '0 4px' }}>ui/BattleScreen.tsx</code>
        / <code>styles/battle-screen.css</code>; the variants here
        are visual references for the three end-states.
      </p>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 32,
          padding: '48px 16px 24px',
        }}
      >
        {variants.map((v) => (
          <div
            key={v.key}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}
          >
            <div
              className={`bt-winner-banner bt-winner-banner--${v.key}`}
              aria-hidden="true"
              style={{ position: 'relative', top: 'auto', left: 'auto', transform: 'none' }}
            >
              <div className="bt-winner-banner__plate">
                <span className="bt-winner-banner__kanji">{v.kanji}</span>
                <span className="bt-winner-banner__divider" />
                <span className="bt-winner-banner__caption">{v.caption}</span>
                <span className="bt-winner-banner__team">{v.team}</span>
              </div>
            </div>
            <span className="tiny-cap" style={{ color: 'var(--ink-fade)' }}>
              {v.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------
 *  Wax-seal sakura stamp — small vermillion disc with a pale
 *  five-petal sakura silhouette. Originally lived in the shop
 *  banner as the title-block avatar; retired May 2026 in favor
 *  of the 商店 shōten chip + hanko-stamp row. Kept exported
 *  from Shop.tsx so future surfaces (battle results, player
 *  profile, ledger entries) can reuse it.
 * ------------------------------------------------------------ */

function SakuraSealShowcase(): ReactElement {
  return (
    <div style={{ display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap' }}>
      {/* Three example sizes — the original banner used 30px; smaller
         variants for inline marks; larger for hero placements. */}
      {[24, 30, 44, 64].map((size) => (
        <div
          key={size}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <div
            style={{
              width: size,
              height: size,
              borderRadius: '50%',
              background: 'var(--vermillion-deep)',
              border: '2.5px solid var(--ink)',
              boxShadow: '0 2.5px 0 var(--ink)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transform: 'rotate(-4deg)',
            }}
          >
            <div style={{ transform: `scale(${(size / 44).toFixed(3)})` }}>
              <SakuraSeal />
            </div>
          </div>
          <span className="tiny-cap" style={{ color: 'var(--ink-fade)' }}>
            {size}px
          </span>
        </div>
      ))}
      <div style={{ maxWidth: 320, marginLeft: 12 }}>
        <p className="hand-note" style={{ fontSize: 13, margin: 0 }}>
          Inline SVG — pale sakura on vermillion wax. Wrap in any
          circle/rotate frame to use as a stamp. Import via{' '}
          <code style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>
            {`import { SakuraSeal } from '../game/Shop'`}
          </code>
          .
        </p>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------
 *  Player identity ema (絵馬) — pentagonal wooden plaque hung
 *  from a vermillion cord. Used in production on the solo shop
 *  page (SoloPlayScreen), where it dangles from the underside
 *  of the seigaiha banner with the player's chosen mascot,
 *  display_name, and team_name. The plaque is portable —
 *  EmaPlaque renders the painted shape + content in normal
 *  flow; the caller owns positioning.
 * ------------------------------------------------------------ */

function PlayerEmaShowcase(): ReactElement {
  // Inline overrides to render the plaque in the gallery's normal
  // flow instead of the fixed-position SoloPlayScreen wrapper, and
  // to disable the sway animation for static comparison. Three
  // example identities show how display_name / team_name / icon
  // combine for different player choices.
  const examples = [
    { icon: 'crane' as const, displayName: 'Akari', teamName: 'Paper Sky' },
    { icon: 'fox' as const, displayName: 'Kage', teamName: 'Tin Wolves' },
    { icon: 'phoenix' as const, displayName: 'Hinata Aino', teamName: 'Ember Court' },
  ]
  return (
    <div style={{ display: 'flex', gap: 36, flexWrap: 'wrap', alignItems: 'flex-start' }}>
      {examples.map((e) => (
        <div
          key={e.icon}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <div className="sps-ema sps-ema--static">
            <EmaPlaque
              icon={e.icon}
              displayName={e.displayName}
              teamName={e.teamName}
            />
          </div>
          <span className="tiny-cap" style={{ color: 'var(--ink-fade)' }}>
            {e.icon} · {e.displayName}
          </span>
        </div>
      ))}
      <div style={{ maxWidth: 280, marginTop: 20 }}>
        <p className="hand-note" style={{ fontSize: 13, margin: 0 }}>
          Pentagonal washi plaque with two vermillion cord strands
          tied through ink eyelet dots at the shoulders. The
          player's chosen mascot stamps the peaked roof as a
          家紋 family crest. In production the cord knot threads
          up into the shop banner; here the plaque sits static
          for inspection. Import:
        </p>
        <p
          className="hand-note"
          style={{ fontSize: 11, fontFamily: 'var(--mono)', marginTop: 6 }}
        >
          {`import { EmaPlaque } from '../ui/SoloPlayScreen'`}
        </p>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------
 *  Saisenbako (賽銭箱) offering box — archived design concept
 *  for the shop's sell zone. Built and pulled May 2026 in favor
 *  of keeping the simpler rice-paper plaque drop target. This
 *  showcase preserves the visual for potential reuse on a
 *  future surface (e.g., a "tithe / donate" interaction at a
 *  shrine event).
 *
 *  Self-contained: all CSS is in the inline <style> block below
 *  under the `.saisen-demo*` prefix so it's insulated from any
 *  future evolution of the production .sell-zone styles.
 * ------------------------------------------------------------ */

function SaisenbakoShowcase(): ReactElement {
  return (
    <div>
      <style>{SAISENBAKO_CSS}</style>
      <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        {(['idle', 'armed', 'over'] as const).map((state) => (
          <div
            key={state}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <div
              className={`saisen-demo${state !== 'idle' ? ` saisen-demo--${state}` : ''}`}
            >
              <svg
                className="saisen-demo__shape"
                viewBox="0 0 360 160"
                preserveAspectRatio="none"
                aria-hidden
              >
                <defs>
                  <pattern
                    id={`saisen-demo-wood-${state}`}
                    width="46"
                    height="160"
                    patternUnits="userSpaceOnUse"
                  >
                    <rect width="46" height="160" fill="#7a4a2a" />
                    <line
                      x1="46"
                      y1="0"
                      x2="46"
                      y2="160"
                      stroke="rgba(0, 0, 0, 0.28)"
                      strokeWidth="1.2"
                    />
                    <line
                      x1="14"
                      y1="0"
                      x2="14"
                      y2="160"
                      stroke="rgba(255, 255, 255, 0.04)"
                      strokeWidth="1"
                    />
                  </pattern>
                </defs>
                <polygon
                  points="16,4 344,4 358,156 2,156"
                  fill={`url(#saisen-demo-wood-${state})`}
                  stroke="var(--ink)"
                  strokeWidth="2.5"
                  strokeLinejoin="round"
                />
              </svg>
              <div className="saisen-demo__slats" aria-hidden>
                <span className="saisen-demo__slat" />
                <span className="saisen-demo__slat" />
                <span className="saisen-demo__slat" />
                <span className="saisen-demo__slat" />
              </div>
              <div className="saisen-demo__shimenawa" aria-hidden>
                <div className="saisen-demo__rope" />
                <div className="saisen-demo__shide saisen-demo__shide--l" />
                <div className="saisen-demo__shide saisen-demo__shide--r" />
              </div>
              <span className="saisen-demo__brand" aria-hidden>売</span>
              <span className="saisen-demo__caption">
                Offer to the Box · refund = tier × star
              </span>
              <div className="saisen-demo__coins" aria-hidden>
                <span className="saisen-demo__coin">◈</span>
                <span className="saisen-demo__coin">◈</span>
                <span className="saisen-demo__coin">◈</span>
              </div>
              {state !== 'idle' && (
                <span className="saisen-demo__refund">+3 ◈</span>
              )}
            </div>
            <span className="tiny-cap" style={{ color: 'var(--ink-fade)' }}>
              {state}
            </span>
          </div>
        ))}
      </div>
      <p
        className="hand-note"
        style={{ fontSize: 13, marginTop: 18, maxWidth: 560 }}
      >
        Wooden offering box you pitch a unit into for gold.
        Trapezoidal flared body with vertical plank lines, slatted
        drop slot, vermillion shimenawa rope with paper shide
        hanging from it, 売 kanji branded into the front, coin
        pile at the base. <strong>armed</strong> trembles the
        shide and pulses a vermillion glow behind the slats;{' '}
        <strong>over</strong> widens the slat gaps (slot opens) and
        lifts the refund pill out of the box.
      </p>
    </div>
  )
}

/** Saisenbako demo CSS — fully scoped under `.saisen-demo` so it
 *  survives any future evolution of the production .sell-zone
 *  styles. Mirrors the May 2026 implementation lifted from Shop.css
 *  before it was pulled. */
const SAISENBAKO_CSS = `
.saisen-demo {
  position: relative;
  width: 360px;
  height: 160px;
  filter: drop-shadow(0 4px 0 var(--ink));
  color: var(--ink);
  transition: transform 0.18s ease;
}
.saisen-demo__shape {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
  pointer-events: none;
}
.saisen-demo__slats {
  position: absolute;
  top: 14px;
  left: 26px;
  right: 26px;
  height: 22px;
  display: flex;
  flex-direction: column;
  gap: 1.5px;
  background: var(--ink);
  border: 1.5px solid var(--ink);
  border-radius: 2px;
  overflow: hidden;
  z-index: 2;
  transition: gap 0.2s ease, background-color 0.2s ease;
}
.saisen-demo__slat {
  flex: 1;
  background: linear-gradient(180deg, #8e5a32 0%, #4a2e18 100%);
}
.saisen-demo__shimenawa {
  position: absolute;
  top: 46px;
  left: 22px;
  right: 22px;
  height: 6px;
  z-index: 3;
  pointer-events: none;
}
.saisen-demo__rope {
  height: 6px;
  background: repeating-linear-gradient(90deg, var(--vermillion-deep) 0 8px, rgba(0, 0, 0, 0.32) 8px 10px);
  border: 1.5px solid var(--ink);
  border-radius: 3px;
  filter: drop-shadow(0 1px 0 var(--ink));
}
.saisen-demo__shide {
  position: absolute;
  top: 4px;
  width: 14px;
  height: 30px;
  background: #fffaee;
  border: 1px solid var(--ink);
  clip-path: polygon(0 0, 100% 0, 100% 28%, 20% 42%, 100% 56%, 20% 70%, 100% 84%, 0 100%);
  transform-origin: top center;
}
.saisen-demo__shide--l { left: 22%; }
.saisen-demo__shide--r { right: 22%; }
.saisen-demo__brand {
  position: absolute;
  top: 64px;
  left: 50%;
  transform: translate(-50%, 0);
  font-family: var(--serif);
  font-size: 58px;
  line-height: 1;
  color: rgba(0, 0, 0, 0.5);
  text-shadow: 0 -1px 0 rgba(255, 240, 220, 0.06), 0 1px 1px rgba(0, 0, 0, 0.45);
  z-index: 1;
  pointer-events: none;
  user-select: none;
}
.saisen-demo__caption {
  position: absolute;
  bottom: 10px;
  left: 18px;
  font-family: var(--hand);
  font-size: 12px;
  color: rgba(255, 245, 220, 0.7);
  letter-spacing: 0.06em;
  z-index: 2;
  pointer-events: none;
}
.saisen-demo__coins {
  position: absolute;
  bottom: 6px;
  right: 16px;
  display: flex;
  align-items: flex-end;
  z-index: 3;
  pointer-events: none;
}
.saisen-demo__coin {
  font-family: var(--serif);
  font-size: 20px;
  line-height: 1;
  color: var(--gold);
  filter: drop-shadow(0 1px 0 var(--ink));
}
.saisen-demo__coin:nth-child(1) { transform: translate(0, 0); }
.saisen-demo__coin:nth-child(2) { transform: translate(-6px, -2px); font-size: 18px; }
.saisen-demo__coin:nth-child(3) { transform: translate(-12px, 1px); font-size: 22px; }
.saisen-demo__refund {
  position: absolute;
  top: -14px;
  right: 18px;
  font-family: var(--mono);
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: var(--ink);
  background: var(--gold);
  border: 2px solid var(--ink);
  border-radius: 999px;
  padding: 4px 12px;
  filter: drop-shadow(0 2px 0 var(--ink));
  white-space: nowrap;
  z-index: 5;
  pointer-events: none;
}
.saisen-demo--armed .saisen-demo__shide {
  animation: saisen-demo-shide-tremble 0.7s ease-in-out infinite;
}
.saisen-demo--armed .saisen-demo__shide--r { animation-delay: -0.35s; }
.saisen-demo--armed .saisen-demo__slats {
  animation: saisen-demo-armed-pulse 1.5s ease-in-out infinite;
}
@keyframes saisen-demo-shide-tremble {
  0%, 100% { transform: rotate(-1.5deg); }
  50%      { transform: rotate(1.5deg); }
}
@keyframes saisen-demo-armed-pulse {
  0%, 100% { background: var(--ink); }
  50%      { background: var(--vermillion-deep); }
}
.saisen-demo--over {
  transform: translateY(2px);
}
.saisen-demo--over .saisen-demo__slats {
  gap: 5px;
  background: var(--vermillion-deep);
  animation: none;
}
.saisen-demo--over .saisen-demo__refund {
  transform: translateY(-4px);
  box-shadow: 0 0 12px rgba(184, 156, 42, 0.6);
}
`

/* ------------------------------------------------------------
 *  Card vocabulary — the recurring marks that decorate
 *  production cards across shop / encyclopedia / battle stage:
 *  vermillion tier-roman hanko, element kanji chip, cost coin
 *  (or FREE flag), animal hanko, type strip. Showcased here as
 *  isolated swatches so designers can compose them into new
 *  surfaces without spelunking through Shop.tsx.
 * ------------------------------------------------------------ */

function CardVocabulary(): ReactElement {
  const swatches: Array<{
    label: string
    note: string
    render: ReactElement
  }> = [
    {
      label: 'Tier roman hanko',
      note: 'Vermillion seal-stamp tier badge — also the encyclopedia tier marker.',
      render: (
        <div
          style={{
            width: 56,
            height: 56,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgb(var(--chiyo-vermillion-bg))',
            color: 'rgb(var(--chiyo-vermillion))',
            border: '3px solid currentColor',
            borderRadius: 10,
            fontFamily: 'var(--mono)',
            fontWeight: 700,
            fontSize: 22,
            letterSpacing: 1,
            transform: 'rotate(-3deg)',
            boxShadow: '0 0 0 2px rgba(255, 250, 235, 0.95) inset',
          }}
        >
          III
        </div>
      ),
    },
    {
      label: 'Element kanji chip',
      note: 'Top-corner element marker. One per element; colors flow from --el-*.',
      render: (
        <div style={{ display: 'flex', gap: 8 }}>
          {(['fire', 'water', 'wood', 'wind', 'earth'] as Element[]).map((el) => {
            const info = ELEMENT_INFO[el]
            return (
              <span
                key={el}
                className="chip"
                style={{
                  background: info.bg,
                  color: info.fg,
                  width: 32,
                  height: 32,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  fontFamily: 'var(--sans)',
                  fontWeight: 700,
                  padding: 0,
                }}
                title={info.en}
              >
                {info.kanji}
              </span>
            )
          })}
        </div>
      ),
    },
    {
      label: 'Cost coin / FREE flag',
      note: 'Bottom-corner. Numeric + ◈ when buyable; “✦ FREE” for bonus slots.',
      render: (
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              padding: '6px 12px',
              background: 'var(--gold-deep)',
              color: 'var(--washi-light)',
              borderRadius: 999,
              fontFamily: 'var(--mono)',
              fontWeight: 700,
              fontSize: 14,
              letterSpacing: 0.5,
            }}
          >
            3 ◈
          </span>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '6px 12px',
              background: 'rgb(var(--chiyo-vermillion))',
              color: 'var(--washi-light)',
              borderRadius: 999,
              fontFamily: 'var(--mono)',
              fontWeight: 700,
              fontSize: 12,
              letterSpacing: 1,
            }}
          >
            ✦ FREE
          </span>
        </div>
      ),
    },
    {
      label: 'Type strip',
      note: 'Mid-card kanji + roman type readout. Element · Animal, mono caps.',
      render: (
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '4px 10px',
            background: 'var(--washi-light)',
            border: '1px solid var(--ink-fade)',
            borderRadius: 4,
            fontFamily: 'var(--mono)',
            fontSize: 10,
            letterSpacing: 1.5,
            color: 'var(--ink)',
            textTransform: 'uppercase',
          }}
        >
          <span>火 FIRE</span>
          <span style={{ opacity: 0.4 }}>·</span>
          <span>獣 MAMMAL</span>
        </div>
      ),
    },
    {
      label: 'Sample full card',
      note: 'The vocabulary composed: tier hanko, element chip, type strip, stats.',
      render: (
        <article
          className="cute-card"
          style={{
            position: 'relative',
            width: 200,
            padding: '14px 14px 18px',
            background: '#ffe2d6',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <span
            className="chip"
            style={{
              position: 'absolute',
              top: -10,
              left: 10,
              background: ELEMENT_INFO.fire.bg,
              color: ELEMENT_INFO.fire.fg,
              width: 30,
              height: 30,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              fontFamily: 'var(--sans)',
                  fontWeight: 700,
              padding: 0,
            }}
            aria-label="element: fire"
          >
            {ELEMENT_INFO.fire.kanji}
          </span>
          <span
            style={{
              position: 'absolute',
              top: -10,
              right: 10,
              width: 30,
              height: 30,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              background: 'rgb(var(--chiyo-vermillion-bg))',
              color: 'rgb(var(--chiyo-vermillion))',
              border: '2.5px solid currentColor',
              fontFamily: 'var(--mono)',
              fontWeight: 700,
              fontSize: 13,
              letterSpacing: 0.5,
              transform: 'rotate(-3deg)',
            }}
            aria-label="tier IV"
          >
            IV
          </span>
          <div style={{ marginTop: 6 }}>
            <TieredCritter glyph="wolf" star={1} size={92} />
          </div>
          <span className="cute-title" style={{ fontSize: 16, marginTop: 2 }}>
            Ash Wolf
          </span>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '3px 8px',
              background: 'var(--washi-light)',
              border: '1px solid var(--ink-fade)',
              borderRadius: 4,
              fontFamily: 'var(--mono)',
              fontSize: 9,
              letterSpacing: 1.5,
              color: 'var(--ink)',
              textTransform: 'uppercase',
            }}
          >
            <span>火 FIRE</span>
            <span style={{ opacity: 0.4 }}>·</span>
            <span>獣 MAMMAL</span>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <HeartStatImg value={9} size={40} />
            <StrengthStatImg value={5} size={40} />
          </div>
        </article>
      ),
    },
  ]
  return (
    <div>
      <p className="hand-note" style={{ marginTop: 0, marginBottom: 24, maxWidth: 720 }}>
        The recurring marks that decorate production cards in the
        shop, encyclopedia, and battle stage. Pulled out as
        isolated swatches so designers can compose them into new
        surfaces without spelunking through Shop.tsx. The full-card
        sample at the end shows how they layer.
      </p>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 16,
        }}
      >
        {swatches.map((s) => (
          <div
            key={s.label}
            className="cute-card"
            style={{
              padding: 20,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 12,
              minHeight: 180,
            }}
          >
            <div
              style={{
                flex: 1,
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '12px 0',
              }}
            >
              {s.render}
            </div>
            <div style={{ width: '100%' }}>
              <div className="tiny-cap" style={{ color: 'var(--ink-fade)', marginBottom: 4 }}>
                {s.label}
              </div>
              <p className="hand-note" style={{ margin: 0, fontSize: 13, lineHeight: 1.4 }}>
                {s.note}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function HeroCraneRow(): ReactElement {
  return (
    <div className="cute-card" style={{ padding: 32, display: 'flex', justifyContent: 'center' }}>
      <HeroCrane size={300} />
    </div>
  )
}

function AnimationShowcase(): ReactElement {
  return (
    <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
      {(['bob', 'wiggle', 'pop'] as const).map((cls) => (
        <AnimCard key={cls} label={`.${cls}`}>
          <div className={cls}>
            <TieredCritter glyph="wolf" star={3} size={88} />
          </div>
        </AnimCard>
      ))}
      <AttackDemoCard side="l" />
      <AttackDemoCard side="r" />
      {/* Flying-variant strike — 28px body lift + attack-strike-*-
          -flying keyframe with +10° forward tilt at peak. Cinderfly
          (butterfly glyph) flown side-by-side against the ground
          wolf so the dive arc reads as a clear delta. */}
      <AttackDemoCard side="l" flying glyph="butterfly" />
      <AttackDemoCard side="r" flying glyph="butterfly" />
      <UnfoldDemoCard />
      <PulseDemoCard kind="healed" />
      <PulseDemoCard kind="buffed-up" />
      <PulseDemoCard kind="buffed-down" />
      <BurstDemoCard kind="clash-burst" />
      <BurstDemoCard kind="super-burst" />
      <BurstDemoCard kind="resist-burst" />
      {/* Ability source-clarity bursts — fire when a unit's trigger
          casts a non-frontline-strike effect. cast-glow halos the
          caster; projectile arcs source→target during the gap before
          consequent damage/heal lands; ability-impact is the target-
          side paper-fold splash; synergy-tag is the midpoint banner
          spawned when the simulator emits a sourceless damage/heal
          (synergy effect). All four are element- or team-tinted. */}
      {(['fire', 'water', 'earth', 'wood', 'wind'] as const).map((el) => (
        <CastGlowDemoCard key={el} element={el} />
      ))}
      <ProjectileDemoCard element="fire" side="l" />
      <ProjectileDemoCard element="wind" side="r" />
      {(['fire', 'water', 'earth', 'wood', 'wind'] as const).map((el) => (
        <AbilityImpactDemoCard key={el} element={el} />
      ))}
      <SynergyTagDemoCard team="A" kanji="水" label="Water Flow ×3" />
      <SynergyTagDemoCard team="B" kanji="業" label="Fire Blaze ×3" />
      <SynergyFlashDemoCard />
      <DeathDemoCard />
    </div>
  )
}

function AnimCard({
  label,
  children,
}: {
  label: string
  children: ReactNode
}): ReactElement {
  return (
    <div
      className="cute-card"
      style={{
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        minWidth: 140,
      }}
    >
      {children}
      <span className="tiny-cap" style={{ color: 'var(--ink-fade)' }}>
        {label}
      </span>
    </div>
  )
}

/** Two-phase attack demo: anticipation windup → strike release. The
 *  outer .attack-windup-* class holds the lean-back pose (forwards),
 *  then the className swaps to .attack-strike-* whose 0% frame
 *  matches the windup's 100% — so the snap forward continues from
 *  the held pose seamlessly. Each tick re-fires the sequence via
 *  remount of the inner body component.
 *
 *  Team A (side="l") lunges to the right; Team B (side="r") lunges
 *  to the left — matching the BattleScreen mapping.
 *
 *  `flying`: when true, swaps the strike keyframe for the
 *  attack-strike-*--flying variant (+10° forward tilt at peak) and
 *  lifts the body 28px so the lunge reads as a diving attack
 *  instead of a ground charge. Matches the BattleUnit's is_flying
 *  rendering path. */
function AttackDemoCard({
  side,
  flying = false,
  glyph = 'wolf',
}: {
  side: 'l' | 'r'
  flying?: boolean
  glyph?: GlyphKey
}): ReactElement {
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const handle = window.setInterval(() => setTick((n) => n + 1), 1800)
    return () => window.clearInterval(handle)
  }, [])
  const label = flying
    ? `attack ${side === 'l' ? 'A' : 'B'} · flying`
    : `attack ${side === 'l' ? 'A' : 'B'}`
  return (
    <AnimCard label={label}>
      <div
        style={{
          width: 100,
          height: 100,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'visible',
        }}
      >
        {/* key remount restarts the two-phase sequence */}
        <AttackDemoBody key={tick} side={side} flying={flying} glyph={glyph} />
      </div>
      <button
        className="cute-btn"
        style={{ fontSize: 11, padding: '4px 10px' }}
        onClick={() => setTick((n) => n + 1)}
      >
        ▶ replay
      </button>
    </AnimCard>
  )
}

function AttackDemoBody({
  side,
  flying,
  glyph,
}: {
  side: 'l' | 'r'
  flying: boolean
  glyph: GlyphKey
}): ReactElement {
  const [phase, setPhase] = useState<'windup' | 'strike'>('windup')
  useEffect(() => {
    // After the 150ms windup completes, swap to the strike phase.
    const handle = window.setTimeout(() => setPhase('strike'), 150)
    return () => window.clearTimeout(handle)
  }, [])
  // Combine the team-B mirror flip with the flying lift on a wrapper
  // separate from the animated class — same split as BattleUnit, so
  // the strike keyframes don't clobber the static transform.
  const FLYING_LIFT_PX = 28
  const innerTransform = `${side === 'r' ? 'scaleX(-1) ' : ''}translateY(${flying ? -FLYING_LIFT_PX : 0}px)`
  const strikeClass = flying ? `attack-strike-${side}--flying` : `attack-strike-${side}`
  const className = phase === 'windup' ? `attack-windup-${side}` : strikeClass
  return (
    <div className={className}>
      <div style={{ transform: innerTransform }}>
        <TieredCritter glyph={glyph} star={3} size={88} />
      </div>
    </div>
  )
}

/** Pulse demo for healed / buffed-up / buffed-down. These are
 *  filter-only animations that stack with bob in production — the
 *  demo shows them on a static wolf so the glow alone is visible.
 *  Same key-remount + ▶ replay pattern as the other demos. */
function PulseDemoCard({ kind }: { kind: 'healed' | 'buffed-up' | 'buffed-down' }): ReactElement {
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const handle = window.setInterval(() => setTick((n) => n + 1), 2000)
    return () => window.clearInterval(handle)
  }, [])
  return (
    <AnimCard label={`.${kind}`}>
      <div
        style={{
          width: 100,
          height: 100,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'visible',
        }}
      >
        <div key={tick} className={kind}>
          <TieredCritter glyph="wolf" star={3} size={88} />
        </div>
      </div>
      <button
        className="cute-btn"
        style={{ fontSize: 11, padding: '4px 10px' }}
        onClick={() => setTick((n) => n + 1)}
      >
        ▶ replay
      </button>
    </AnimCard>
  )
}

/** Synergy badge flash demo. In production the .synergy-flash class
 *  runs once on chip mount; here we force remount via a tick so the
 *  flash loops for inspection. */
function SynergyFlashDemoCard(): ReactElement {
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const handle = window.setInterval(() => setTick((n) => n + 1), 1500)
    return () => window.clearInterval(handle)
  }, [])
  return (
    <AnimCard label=".synergy-flash">
      <div
        style={{
          width: 100,
          height: 100,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <span
          key={tick}
          className="chip synergy-flash"
          style={{ background: 'var(--mint)', fontSize: 11 }}
        >
          wolf 3
        </span>
      </div>
      <button
        className="cute-btn"
        style={{ fontSize: 11, padding: '4px 10px' }}
        onClick={() => setTick((n) => n + 1)}
      >
        ▶ replay
      </button>
    </AnimCard>
  )
}

/** Impact burst demos — clash flash, super-effective burst, resisted
 *  thud. In production these spawn at strike/damage events; the
 *  demo renders them on a static backdrop so the shape and timing
 *  are visible on their own. */
function BurstDemoCard({
  kind,
}: {
  kind: 'clash-burst' | 'super-burst' | 'resist-burst'
}): ReactElement {
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const handle = window.setInterval(() => setTick((n) => n + 1), 1200)
    return () => window.clearInterval(handle)
  }, [])
  return (
    <AnimCard label={`.${kind}`}>
      <div
        style={{
          position: 'relative',
          width: 100,
          height: 100,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'visible',
          background: 'var(--washi-light)',
          border: '1px dashed var(--ink-fade)',
          borderRadius: 12,
        }}
      >
        <div
          key={tick}
          className={kind}
          style={{ left: '50%', top: '50%' }}
        />
      </div>
      <button
        className="cute-btn"
        style={{ fontSize: 11, padding: '4px 10px' }}
        onClick={() => setTick((n) => n + 1)}
      >
        ▶ replay
      </button>
    </AnimCard>
  )
}

// Element → RGB triple used by the ability bursts in production
// (mirrors BattleScreen.tsx). Repeated here so the gallery doesn't
// depend on the ui module. Keep these two in sync.
const DEMO_ELEMENT_RGB: Record<string, string> = {
  fire:  '196, 64, 48',
  water: '38, 78, 124',
  earth: '160, 110, 60',
  wood:  '70, 130, 80',
  wind:  '170, 140, 200',
}

/** Cast-glow halo demo — pulsing ring around an ability-casting unit
 *  during the gap between trigger_fired and the consequent damage/heal.
 *  In production the ring is tinted from the source unit's element via
 *  the --cast-glow-rgb CSS variable; here we set it per card so all
 *  five elemental tints are visible at a glance. */
function CastGlowDemoCard({ element }: { element: 'fire' | 'water' | 'earth' | 'wood' | 'wind' }): ReactElement {
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const handle = window.setInterval(() => setTick((n) => n + 1), 1500)
    return () => window.clearInterval(handle)
  }, [])
  return (
    <AnimCard label={`.cast-glow · ${element}`}>
      <div
        style={{
          width: 100,
          height: 100,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'visible',
        }}
      >
        <div
          key={tick}
          className="cast-glow"
          style={{ ['--cast-glow-rgb' as unknown as keyof CSSProperties]: DEMO_ELEMENT_RGB[element] } as CSSProperties}
        >
          <TieredCritter glyph="wolf" star={3} size={88} />
        </div>
      </div>
      <button
        className="cute-btn"
        style={{ fontSize: 11, padding: '4px 10px' }}
        onClick={() => setTick((n) => n + 1)}
      >
        ▶ replay
      </button>
    </AnimCard>
  )
}

/** Projectile arc demo — source→target spark used during the
 *  trigger_fired → damage/heal gap. Production uses Framer Motion
 *  to animate `left` from the source slot to the target slot while
 *  the keyframe handles the vertical arc + scale + opacity. The
 *  demo mirrors that: animate `left` over a fixed delta inside a
 *  card-local backdrop. `side='l'` flies left→right (team A casts);
 *  `side='r'` flies right→left (team B casts). */
function ProjectileDemoCard({
  element,
  side,
}: {
  element: 'fire' | 'water' | 'earth' | 'wood' | 'wind'
  side: 'l' | 'r'
}): ReactElement {
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const handle = window.setInterval(() => setTick((n) => n + 1), 1400)
    return () => window.clearInterval(handle)
  }, [])
  const rgb = DEMO_ELEMENT_RGB[element]
  const fromPct = side === 'l' ? 15 : 85
  const toPct = side === 'l' ? 85 : 15
  return (
    <AnimCard label={`.projectile · ${element} ${side === 'l' ? '→' : '←'}`}>
      <div
        style={{
          position: 'relative',
          width: 140,
          height: 100,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'visible',
          background: 'var(--washi-light)',
          border: '1px dashed var(--ink-fade)',
          borderRadius: 12,
        }}
      >
        {/* Source + target markers so the flight direction is legible. */}
        <span
          style={{
            position: 'absolute', left: `${fromPct}%`, top: '50%',
            transform: 'translate(-50%, -50%)',
            width: 10, height: 10, borderRadius: 999,
            background: `rgba(${rgb}, 0.35)`,
            border: `1.5px solid rgba(${rgb}, 0.8)`,
          }}
        />
        <span
          style={{
            position: 'absolute', left: `${toPct}%`, top: '50%',
            transform: 'translate(-50%, -50%)',
            width: 14, height: 14, borderRadius: 999,
            background: `rgba(${rgb}, 0.15)`,
            border: `1.5px dashed rgba(${rgb}, 0.6)`,
          }}
        />
        <motion.div
          key={tick}
          className="projectile"
          initial={{ left: `${fromPct}%`, top: '50%' }}
          animate={{ left: `${toPct}%` }}
          transition={{ duration: 0.34, ease: 'easeInOut' }}
          style={{ ['--proj-rgb' as unknown as keyof CSSProperties]: rgb } as CSSProperties}
        />
      </div>
      <button
        className="cute-btn"
        style={{ fontSize: 11, padding: '4px 10px' }}
        onClick={() => setTick((n) => n + 1)}
      >
        ▶ replay
      </button>
    </AnimCard>
  )
}

/** Ability impact demo — paper-fold splash that lands at the target
 *  the moment an ability's consequent damage/heal resolves. Element-
 *  tinted via --proj-rgb (matches the projectile that delivered it
 *  in production). */
function AbilityImpactDemoCard({ element }: { element: 'fire' | 'water' | 'earth' | 'wood' | 'wind' }): ReactElement {
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const handle = window.setInterval(() => setTick((n) => n + 1), 1200)
    return () => window.clearInterval(handle)
  }, [])
  return (
    <AnimCard label={`.ability-impact · ${element}`}>
      <div
        style={{
          position: 'relative',
          width: 100,
          height: 100,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'visible',
          background: 'var(--washi-light)',
          border: '1px dashed var(--ink-fade)',
          borderRadius: 12,
        }}
      >
        <div
          key={tick}
          className="ability-impact"
          style={{
            left: '50%', top: '50%',
            ['--proj-rgb' as unknown as keyof CSSProperties]: DEMO_ELEMENT_RGB[element],
          } as CSSProperties}
        />
      </div>
      <button
        className="cute-btn"
        style={{ fontSize: 11, padding: '4px 10px' }}
        onClick={() => setTick((n) => n + 1)}
      >
        ▶ replay
      </button>
    </AnimCard>
  )
}

/** Synergy-tag banner demo — midpoint label naming the synergy
 *  responsible for an otherwise sourceless damage/heal event.
 *  Team-colored: A = indigo, B = vermillion. In production the
 *  handler picks the most likely active synergy and the banner
 *  spawns at battlefield midpoint, slightly above the strike row. */
function SynergyTagDemoCard({
  team,
  kanji,
  label,
}: {
  team: 'A' | 'B'
  kanji: string
  label: string
}): ReactElement {
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const handle = window.setInterval(() => setTick((n) => n + 1), 1800)
    return () => window.clearInterval(handle)
  }, [])
  const teamColor = team === 'A' ? 'rgb(38, 78, 124)' : 'rgb(196, 64, 48)'
  return (
    <AnimCard label={`.synergy-tag · ${team}`}>
      <div
        style={{
          position: 'relative',
          width: 160,
          height: 100,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'visible',
          background: 'var(--washi-light)',
          border: '1px dashed var(--ink-fade)',
          borderRadius: 12,
        }}
      >
        <div
          key={tick}
          className="synergy-tag"
          style={{ left: '50%', top: '50%', ['--syn-tag-color' as unknown as keyof CSSProperties]: teamColor } as CSSProperties}
        >
          <span className="synergy-tag__kanji">{kanji}</span>
          {label}
        </div>
      </div>
      <button
        className="cute-btn"
        style={{ fontSize: 11, padding: '4px 10px' }}
        onClick={() => setTick((n) => n + 1)}
      >
        ▶ replay
      </button>
    </AnimCard>
  )
}

/** One-shot summon unfold demo. Same key-remount + ▶ replay pattern
 *  as the attack/death demos. Plays the .unfold-in keyframe — a
 *  paper-scrap unfolding into the unit silhouette. In battle, the
 *  summon handler sets visualState='summoning'; useReplay reverts
 *  to 'idle' after the animation completes. */
function UnfoldDemoCard(): ReactElement {
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const handle = window.setInterval(() => setTick((n) => n + 1), 2400)
    return () => window.clearInterval(handle)
  }, [])
  return (
    <AnimCard label=".unfold-in">
      <div
        key={tick}
        style={{
          position: 'relative',
          width: 100,
          height: 100,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'visible',
        }}
      >
        {/* Paper photo stages (siblings — they don't inherit the
            glyph's transform). They cross-fade in order, rising
            from the floor as each fold completes, then yield to
            the glyph for the final reveal. */}
        <img className="unfold-stage-flat"  src="/glyph-assets/unfold-flat.png"  alt="" />
        <img className="unfold-stage-fold1" src="/glyph-assets/unfold-fold1.png" alt="" />
        <img className="unfold-stage-fold2" src="/glyph-assets/unfold-fold2.png" alt="" />
        <img className="unfold-stage-fold3" src="/glyph-assets/unfold-fold3.png" alt="" />
        <div className="unfold-in">
          <TieredCritter glyph="wolf" star={3} size={88} />
        </div>
      </div>
      <button
        className="cute-btn"
        style={{ fontSize: 11, padding: '4px 10px' }}
        onClick={() => setTick((n) => n + 1)}
      >
        ▶ replay
      </button>
    </AnimCard>
  )
}

/** One-shot death/crumple demo. Same key-remount pattern as
 *  AttackDemoCard: .death-fold has animation-fill-mode: forwards so
 *  the unit holds invisible after the fold; bumping the tick remounts
 *  the node and replays from the top. Slightly longer interval than
 *  the attack demo to leave breathing room after the unit vanishes. */
function DeathDemoCard(): ReactElement {
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const handle = window.setInterval(() => setTick((n) => n + 1), 3000)
    return () => window.clearInterval(handle)
  }, [])
  return (
    <AnimCard label=".death-fold">
      <div
        key={tick}
        style={{
          position: 'relative',
          width: 100,
          height: 100,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'visible',
        }}
      >
        <div className="death-fold">
          <TieredCritter glyph="wolf" star={3} size={88} />
        </div>
        <img
          className="death-ball"
          src="/glyph-assets/crumpled-ball.png"
          alt=""
        />
      </div>
      <button
        className="cute-btn"
        style={{ fontSize: 11, padding: '4px 10px' }}
        onClick={() => setTick((n) => n + 1)}
      >
        ▶ replay
      </button>
    </AnimCard>
  )
}

interface EvoVariant {
  key: string
  label: string
  note: string
  sourceA: EvolutionUnit
  sourceB: EvolutionUnit
  result: EvolutionUnit
  tickedDot: 1 | 2 | 3
  starUp: boolean
  durationMs: number
}

const CRANE_FLOCK = {
  glyph: 'crane' as const,
  name: 'Crane Flock',
  element: 'wind' as const,
}

const EVO_VARIANTS: EvoVariant[] = [
  {
    key: 'fill-1',
    label: 'Fill 1/3',
    note: '★1 + ★1 → ★1, counter 0/3 → 1/3',
    sourceA: { ...CRANE_FLOCK, hp: 5, atk: 4, star: 1 },
    sourceB: { ...CRANE_FLOCK, hp: 5, atk: 4, star: 1 },
    result:  { ...CRANE_FLOCK, hp: 6, atk: 5, star: 1 },
    tickedDot: 1,
    starUp: false,
    durationMs: 2600,
  },
  {
    key: 'fill-2',
    label: 'Fill 2/3',
    note: '★1 + ★1 → ★1, counter 1/3 → 2/3',
    sourceA: { ...CRANE_FLOCK, hp: 6, atk: 5, star: 1 },
    sourceB: { ...CRANE_FLOCK, hp: 5, atk: 4, star: 1 },
    result:  { ...CRANE_FLOCK, hp: 7, atk: 6, star: 1 },
    tickedDot: 2,
    starUp: false,
    durationMs: 2600,
  },
  {
    key: 'star-1-2',
    label: '★1 → ★2',
    note: '★1 + ★1 → ★2, counter 2/3 → 3/3, star up',
    sourceA: { ...CRANE_FLOCK, hp: 7, atk: 6, star: 1 },
    sourceB: { ...CRANE_FLOCK, hp: 5, atk: 4, star: 1 },
    result:  { ...CRANE_FLOCK, hp: 8, atk: 7, star: 2 },
    tickedDot: 3,
    starUp: true,
    durationMs: 4200,
  },
  {
    key: 'star-2-3',
    label: '★2 → ★3',
    note: '★2 + ★2 → ★3, counter 2/3 → 3/3, star up',
    sourceA: { ...CRANE_FLOCK, hp: 8, atk: 7, star: 2 },
    sourceB: { ...CRANE_FLOCK, hp: 7, atk: 6, star: 2 },
    result:  { ...CRANE_FLOCK, hp: 9, atk: 8, star: 3 },
    tickedDot: 3,
    starUp: true,
    durationMs: 4200,
  },
]

function EvolutionDemo(): ReactElement {
  const [variantKey, setVariantKey] = useState(EVO_VARIANTS[2]!.key)
  const [runId, setRunId] = useState(0)
  const variant = EVO_VARIANTS.find((v) => v.key === variantKey) ?? EVO_VARIANTS[0]!

  const play = (key: string): void => {
    setVariantKey(key)
    setRunId((r) => r + 1)
  }

  return (
    <div>
      <p className="hand-note" style={{ marginTop: 0, marginBottom: 14, maxWidth: 720 }}>
        The combine animation per spec §4.4. Two same-unit, same-star copies
        arc into the center, fuse, and a result card ejects. Star-up combines
        add the gold burst, white flash, sparkle ring, and arrow trails;
        fill-tick combines play the lighter version (counter pulse + arc-in
        + eject only).
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
        {EVO_VARIANTS.map((v) => {
          const active = v.key === variantKey
          return (
            <button
              key={v.key}
              className={`cute-btn ${active ? 'gold' : ''}`}
              onClick={() => play(v.key)}
              title={v.note}
            >
              {v.label}
            </button>
          )
        })}
      </div>
      <EvolutionSequence
        key={`${variant.key}-${runId}`}
        sourceA={variant.sourceA}
        sourceB={variant.sourceB}
        result={variant.result}
        tickedDot={variant.tickedDot}
        starUp={variant.starUp}
        durationMs={variant.durationMs}
      />
      <p
        className="tiny-cap"
        style={{ color: 'var(--ink-fade)', marginTop: 10 }}
      >
        {variant.note}
      </p>
    </div>
  )
}

/* ============================================================ */
/* ⑩b Forged-frame card — alt art direction                     */
/* ============================================================ */
/*
 * Single painted card frame (heart icon + fist icon + tier badge
 * baked into the chrome) with only the unit-specific values
 * overlaid: name, tier numeral, glyph, HP, ATK.
 *
 * This is the opposite philosophy from the current production
 * ShopCard, where stat ICONS are sprites (HeartStatImg /
 * StrengthStatImg) composited on top of a CSS-drawn card. Here,
 * the ICONS are part of the frame; only the NUMBERS are dynamic.
 *
 * Assets: /ui-assets/{wind,wood}-card-frame-{1,2,3}.webp. Frame asset
 * keyed by element + star — chrome grows richer per combine, and each
 * element family has its own painted motif (wind = gold trim + origami
 * accents; wood = green frame + leaf/vine accents). Elements without
 * shipped art (fire / water / earth) fall back to wind via the
 * forgedFrameUrl helper.
 * The heart/fist icons (and therefore the HP/ATK overlay positions)
 * sit in identical proportions across all three frames, so the same
 * percentage-based number overlay works at every star.
 */

interface ForgedCardData {
  name: string
  glyph: GlyphKey
  animal: Animal
  element: Element
  tier: 1 | 2 | 3 | 4 | 5
  star: 1 | 2 | 3
  hp: number
  atk: number
}

/** Map a unit's element + star to the painted forged-frame asset URL.
 *  Mirrors forgedFrameUrl in Shop.tsx — all five element families
 *  ship today. */
function forgedFrameUrl(element: Element, star: 1 | 2 | 3): string {
  return `/ui-assets/${element}-card-frame-${star}.webp`
}

/** Per-element overlay anchor positions. The painted slots on each
 *  frame family land at slightly different y/x percentages depending
 *  on the motif, so each element can shift specific overlays without
 *  touching the others.
 *
 *  Keep these in sync with the .shop-card--el-{element} +
 *  .board-slot--el-{element} CSS overrides in Shop.css — those are
 *  authoritative for production; this lookup is the gallery preview.
 *  An empty entry uses defaults. */
interface ForgeOverlayPos {
  nameTop: string       // banner — name (shop) / stars (board)
  tierTop: string
  tierRight: string
  hpBottom: string
  hpLeft: string
  atkBottom: string
  atkRight: string
  chipBottom: string    // shop animal chip
  fillBottom: string    // board fill counter
}

const FORGE_OVERLAY_DEFAULTS: ForgeOverlayPos = {
  nameTop: '8%',
  tierTop: '6%',
  tierRight: '6%',
  hpBottom: '11%',
  hpLeft: '9%',
  atkBottom: '11%',
  atkRight: '8%',
  chipBottom: '4%',
  fillBottom: '7%',
}

const FORGE_OVERLAY_PER_ELEMENT: Partial<Record<Element, Partial<ForgeOverlayPos>>> = {
  // wind: defaults
  wood: {
    nameTop: '10%',  // banner shelf lower on wood frames
    tierTop: '7%',   // tier circle sits ~1% lower on wood
    hpBottom: '8%',  // HP — wood's heart icon sits lower + inboard
    hpLeft: '8%',
    atkBottom: '9%', // ATK — wood's fist icon sits lower
    chipBottom: '2.5%', // animal chip — wood's bottom leaf row sits lower
  },
  earth: {
    hpBottom: '10%',  // HP nudged down + left to track earth's heart icon
    hpLeft: '8%',
    atkBottom: '10%', // ATK nudged down to track earth's fist icon
    chipBottom: '3%', // animal chip — earth's bottom edge sits lower
  },
  water: {
    nameTop: '7%',     // banner shelf ~1% higher on water frames
    tierTop: '5%',     // tier disc nudged up + right
    tierRight: '5%',
    hpBottom: '10%',   // HP down + right to track water's heart icon
    hpLeft: '10%',
    atkBottom: '10%',  // ATK down + left to track water's fist icon
    atkRight: '9%',
    chipBottom: '3%',  // animal chip — water's bottom ribbon sits lower
  },
  fire: {
    // Fire's painted-slot layout differs more than other elements
    // (scroll banner top, hex tier badge, mid-side heart/fist).
    nameTop: '8.5%',    // scroll banner sits ~0.5% lower
    tierTop: '6.5%',    // hex tier badge nudged right + slightly down
    tierRight: '5%',
    hpBottom: '7.5%',   // HP far left + down — heart sits mid-side
    hpLeft: '7%',
    atkBottom: '8.5%',  // ATK down — fist sits mid-side
    chipBottom: '3.5%', // animal chip nudged slightly down on fire
  },
}

function forgeOverlayPos(element: Element): ForgeOverlayPos {
  return {
    ...FORGE_OVERLAY_DEFAULTS,
    ...(FORGE_OVERLAY_PER_ELEMENT[element] ?? {}),
  }
}

const FORGED_SAMPLES: ForgedCardData[] = [
  { name: 'Sky Ray',  glyph: 'ray',     animal: 'fish', element: 'wind', tier: 2, star: 1, hp: 6,  atk: 3 },
  { name: 'Sage Owl', glyph: 'owl',     animal: 'bird', element: 'wind', tier: 2, star: 2, hp: 14, atk: 6 },
  { name: 'Phoenix',  glyph: 'phoenix', animal: 'bird', element: 'wind', tier: 3, star: 3, hp: 22, atk: 9 },
]

const FORGED_WOOD_SAMPLES: ForgedCardData[] = [
  { name: 'Acorn Bear',   glyph: 'bear',     animal: 'mammal',  element: 'wood', tier: 1, star: 1, hp: 5,  atk: 2 },
  { name: 'Vine Serpent', glyph: 'serpent',  animal: 'reptile', element: 'wood', tier: 2, star: 2, hp: 12, atk: 5 },
  { name: 'Spore Tadpole',glyph: 'tadpole',  animal: 'reptile', element: 'wood', tier: 3, star: 3, hp: 18, atk: 8 },
]

const FORGED_EARTH_SAMPLES: ForgedCardData[] = [
  { name: 'Pebble Pup',     glyph: 'pup',      animal: 'mammal', element: 'earth', tier: 1, star: 1, hp: 7,  atk: 1 },
  { name: 'Anvil Boar',     glyph: 'boar',     animal: 'mammal', element: 'earth', tier: 2, star: 2, hp: 14, atk: 4 },
  { name: 'Stone Tortoise', glyph: 'tortoise', animal: 'reptile', element: 'earth', tier: 3, star: 3, hp: 24, atk: 6 },
]

const FORGED_WATER_SAMPLES: ForgedCardData[] = [
  { name: 'Stream Smelt', glyph: 'smelt',  animal: 'fish',    element: 'water', tier: 1, star: 1, hp: 4,  atk: 3 },
  { name: 'Marsh Caiman', glyph: 'caiman', animal: 'reptile', element: 'water', tier: 2, star: 2, hp: 11, atk: 6 },
  { name: 'Tide Whale',   glyph: 'whale',  animal: 'mammal',  element: 'water', tier: 3, star: 3, hp: 22, atk: 7 },
]

const FORGED_FIRE_SAMPLES: ForgedCardData[] = [
  { name: 'Ember Moth',   glyph: 'moth',     animal: 'insect',  element: 'fire', tier: 1, star: 1, hp: 3, atk: 4 },
  { name: 'Tinder Fox',   glyph: 'fox',      animal: 'mammal',  element: 'fire', tier: 2, star: 2, hp: 9, atk: 6 },
  { name: 'Cinder Wyrm',  glyph: 'wyrm',     animal: 'reptile', element: 'fire', tier: 3, star: 3, hp: 17, atk: 9 },
]

// Synthetic max-length probe (15 chars, the hard schema cap on
// unit.name). Not a real unit — picked to visually represent the
// ceiling for future name authoring. The longest real unit name in
// the catalog after the Salamander rename is 14 chars (Stone
// Tortoise, Mantis Acolyte).
const LONGEST_NAME_SAMPLE: ForgedCardData = {
  name: 'Iron Salamander',
  glyph: 'salamander',
  element: 'fire',
  animal: 'reptile',
  tier: 2,
  star: 2,
  hp: 5,
  atk: 7,
}

function ForgedFrameCards(): ReactElement {
  return (
    <div>
      <p
        className="hand-note"
        style={{ marginBottom: 18, fontSize: 14, maxWidth: 720 }}
      >
        Alternative art direction: the entire card chrome — frame,
        decoration, AND stat icons (heart + fist) — is one painted asset.
        Only the unit-specific values get overlaid: name banner, tier
        numeral, the origami glyph in the center, and the two stat numbers.
        Comparable to a tarot or Pokémon card where the artwork ships as a
        single illustrated frame and the printed text is the only variable.
      </p>
      <div
        style={{
          display: 'flex',
          gap: 28,
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          marginBottom: 18,
        }}
      >
        {FORGED_SAMPLES.map((u, i) => (
          <ForgedCard key={u.name} data={u} width={240} bobDelay={i * 0.3} />
        ))}
      </div>
      <p
        className="tiny-cap"
        style={{ color: 'var(--ink-fade)', marginBottom: 8 }}
      >
        ↓ Wood family — same overlays, different painted motif
      </p>
      <div
        style={{
          display: 'flex',
          gap: 28,
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          marginBottom: 18,
        }}
      >
        {FORGED_WOOD_SAMPLES.map((u, i) => (
          <ForgedCard key={u.name} data={u} width={240} bobDelay={i * 0.3} />
        ))}
      </div>
      <p
        className="tiny-cap"
        style={{ color: 'var(--ink-fade)', marginBottom: 8 }}
      >
        ↓ Earth family — stone / jagged-rock motif
      </p>
      <div
        style={{
          display: 'flex',
          gap: 28,
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          marginBottom: 18,
        }}
      >
        {FORGED_EARTH_SAMPLES.map((u, i) => (
          <ForgedCard key={u.name} data={u} width={240} bobDelay={i * 0.3} />
        ))}
      </div>
      <p
        className="tiny-cap"
        style={{ color: 'var(--ink-fade)', marginBottom: 8 }}
      >
        ↓ Water family — wave / origami ribbon motif
      </p>
      <div
        style={{
          display: 'flex',
          gap: 28,
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          marginBottom: 18,
        }}
      >
        {FORGED_WATER_SAMPLES.map((u, i) => (
          <ForgedCard key={u.name} data={u} width={240} bobDelay={i * 0.3} />
        ))}
      </div>
      <p
        className="tiny-cap"
        style={{ color: 'var(--ink-fade)', marginBottom: 8 }}
      >
        ↓ Fire family — scroll banner + flame / leaf motif
      </p>
      <div
        style={{
          display: 'flex',
          gap: 28,
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          marginBottom: 32,
        }}
      >
        {FORGED_FIRE_SAMPLES.map((u, i) => (
          <ForgedCard key={u.name} data={u} width={240} bobDelay={i * 0.3} />
        ))}
      </div>
      <p
        className="tiny-cap"
        style={{ color: 'var(--ink-fade)', marginBottom: 4 }}
      >
        ↓ Name-length stress test
      </p>
      <p
        className="hand-note"
        style={{
          color: 'var(--ink-soft)',
          fontSize: 12,
          marginBottom: 12,
          maxWidth: 720,
        }}
      >
        Schema caps <code>unit.name</code> at <strong>15 characters</strong>{' '}
        (incl. spaces). The synthetic probe below ("Iron Salamander", 15
        chars) shows the ceiling — the banner is clamped between roughly
        18%–78% of card width with{' '}
        <code>white-space: nowrap; overflow: hidden; text-overflow: ellipsis</code>.
        Cards rendered below at the reference size (240px) and the 60% /
        40% compact sizes so the ellipsis cut-off point is visible at
        each scale. Real catalog max after the Salamander rename is 14
        chars (Stone Tortoise, Mantis Acolyte).
      </p>
      <div
        style={{
          display: 'flex',
          gap: 28,
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          marginBottom: 32,
        }}
      >
        <ForgedCard data={LONGEST_NAME_SAMPLE} width={240} bobDelay={0} />
        <ForgedCard data={LONGEST_NAME_SAMPLE} width={144} bobDelay={0.15} />
        <ForgedCard data={LONGEST_NAME_SAMPLE} width={96} bobDelay={0.3} />
      </div>
      <p
        className="tiny-cap"
        style={{ color: 'var(--ink-fade)', marginBottom: 12 }}
      >
        ↓ Same cards at ~60% scale (compact roster size)
      </p>
      <div
        style={{
          display: 'flex',
          gap: 18,
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          marginBottom: 32,
        }}
      >
        {FORGED_SAMPLES.map((u, i) => (
          <ForgedCard
            key={`sm-${u.name}`}
            data={u}
            width={144}
            bobDelay={i * 0.3}
          />
        ))}
      </div>
      <p
        className="tiny-cap"
        style={{ color: 'var(--ink-fade)', marginBottom: 4 }}
      >
        ↓ Mobile shop strip — 96px wide (matches .shop-card--compact),
        6px gap, 5-card row mimicking the actual mobile drafting row
      </p>
      <p
        className="hand-note"
        style={{
          color: 'var(--ink-soft)',
          fontSize: 12,
          marginBottom: 12,
          maxWidth: 720,
        }}
      >
        At 96px / 40% scale: name banner becomes ~5px (illegible), tier
        numeral ~6px, stat numbers ~15px (readable), animal-chip text
        ~4px (illegible). The frame chrome + heart/fist + stat numbers
        survive; the text overlays don't. To ship at this size we'd
        either drop the name/tier/chip overlays on compact (let the
        glyph + numbers do all the work), or add a floor to the s()
        scaler so text never falls below a readable minimum.
      </p>
      <div
        style={{
          background: 'rgba(58, 42, 24, 0.04)',
          border: '1.5px dashed rgba(58, 42, 24, 0.22)',
          borderRadius: 12,
          padding: '18px 8px 8px',
          display: 'flex',
          gap: 6,
          justifyContent: 'center',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
        }}
      >
        {/* Repeat the 3 samples to build a 5-card row, matching the
            real shop's 5 base slots. */}
        {[0, 1, 2, 0, 1].map((idx, i) => (
          <ForgedCard
            key={`shop-${i}`}
            data={FORGED_SAMPLES[idx]!}
            width={96}
            bobDelay={i * 0.25}
          />
        ))}
      </div>

      {/* === Board-card variant ================================== */}
      <p
        className="tiny-cap"
        style={{ color: 'var(--ink-fade)', marginTop: 32, marginBottom: 4 }}
      >
        ↓ Board-card variant — owned units on the lineup / bench
      </p>
      <p
        className="hand-note"
        style={{
          color: 'var(--ink-soft)',
          fontSize: 12,
          marginBottom: 12,
          maxWidth: 720,
        }}
      >
        Two zone swaps vs the shop card:{' '}
        <strong>name banner → star indicator</strong> (✦ × current star) and{' '}
        <strong>animal chip → fill indicator</strong> (3-step combine pip
        counter; capped at ★3/fill=3 turns the pips gold). Frame asset is
        the per-star webp (★1 / ★2 / ★3) so the chrome itself escalates
        with combining. Tier numeral + element disc kept — element
        matters in battle, tier still differentiates roster slots.
      </p>
      <div
        style={{
          display: 'flex',
          gap: 18,
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          marginBottom: 16,
        }}
      >
        {BOARD_SAMPLES.map((u, i) => (
          <ForgedBoardCard
            key={`bd-${i}`}
            data={u}
            width={144}
            bobDelay={i * 0.25}
          />
        ))}
      </div>
      <p
        className="tiny-cap"
        style={{ color: 'var(--ink-fade)', marginBottom: 4 }}
      >
        ↓ Mobile lineup strip — 96px (matches compact roster)
      </p>
      <div
        style={{
          background: 'rgba(58, 42, 24, 0.04)',
          border: '1.5px dashed rgba(58, 42, 24, 0.22)',
          borderRadius: 12,
          padding: '18px 8px 8px',
          display: 'flex',
          gap: 6,
          justifyContent: 'center',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
        }}
      >
        {BOARD_SAMPLES.map((u, i) => (
          <ForgedBoardCard
            key={`bd-sm-${i}`}
            data={u}
            width={96}
            bobDelay={i * 0.25}
          />
        ))}
      </div>
    </div>
  )
}

function ForgedCard({
  data,
  width = 240,
  bobDelay = 0,
}: {
  data: ForgedCardData
  width?: number
  bobDelay?: number
}): ReactElement {
  const ROMAN: Record<1 | 2 | 3 | 4 | 5, string> = {
    1: 'I', 2: 'II', 3: 'III', 4: 'IV', 5: 'V',
  }
  // Everything pixel-valued scales linearly with `width` so the small
  // (~144px) and full-size (240px) variants render at the same visual
  // proportions. Ratios derived from the 240px reference.
  const s = (n: number) => Math.round((n * width) / 240)
  // Outline stroke thickness shrinks with the card too — 2px at 240
  // would look chunky on a 144px card.
  const stroke = Math.max(1, Math.round((2 * width) / 240))
  const outlineShadow = [
    `-${stroke}px -${stroke}px 0 #000`,
    ` ${stroke}px -${stroke}px 0 #000`,
    `-${stroke}px  ${stroke}px 0 #000`,
    ` ${stroke}px  ${stroke}px 0 #000`,
    `0 -${stroke}px 0 #000`,
    `0  ${stroke}px 0 #000`,
    `-${stroke}px 0 0 #000`,
    ` ${stroke}px 0 0 #000`,
    `0 ${Math.max(1, stroke + 1)}px ${stroke + 2}px rgba(0,0,0,0.45)`,
  ].join(', ')
  // One frame asset per star level (★1/★2/★3). The chrome (gold trim,
  // origami accents) escalates with star while the heart/fist icon
  // positions stay identical, so the HP/ATK number overlays don't
  // need to reposition per star — per-ELEMENT shifts come from the
  // forgeOverlayPos lookup.
  const frameUrl = forgedFrameUrl(data.element, data.star)
  const pos = forgeOverlayPos(data.element)
  return (
    <div
      style={{
        width,
        aspectRatio: '2 / 3',
        // Multi-layer background: frame webp ON TOP of a 95%-sized cream
        // washi rectangle. The cream layer is sized smaller than the
        // container because the frame asset has transparent margins
        // around the painted gold border; a full-container cream fill
        // would leak past the visible frame edge.
        backgroundImage:
          `url(${frameUrl}), linear-gradient(var(--washi-light), var(--washi-light))`,
        backgroundSize: '100% 100%, 85% 85%',
        backgroundPosition: 'center, center',
        backgroundRepeat: 'no-repeat, no-repeat',
        position: 'relative',
        filter: `drop-shadow(0 ${s(8)}px ${s(14)}px rgba(60, 36, 12, 0.22))`,
      }}
    >
      {/* Top banner — unit name */}
      <div
        style={{
          position: 'absolute',
          top: pos.nameTop,
          left: '18%',
          right: '22%',
          height: '7%',
          display: 'grid',
          placeItems: 'center',
          fontFamily: 'var(--serif)',
          fontSize: s(13),
          color: 'var(--ink)',
          letterSpacing: '0.04em',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          pointerEvents: 'none',
        }}
      >
        {data.name}
      </div>

      {/* Top-right circle — tier numeral (Roman, matching codex seal) */}
      <div
        style={{
          position: 'absolute',
          top: pos.tierTop,
          right: pos.tierRight,
          width: '14%',
          height: '10%',
          display: 'grid',
          placeItems: 'center',
          fontFamily: 'var(--serif)',
          fontWeight: 700,
          fontSize: s(16),
          color: 'var(--ink)',
          letterSpacing: '0.02em',
          pointerEvents: 'none',
        }}
      >
        {ROMAN[data.tier]}
      </div>

      {/* Center art — origami glyph */}
      <div
        style={{
          position: 'absolute',
          top: '22%',
          left: '14%',
          right: '14%',
          bottom: '24%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        <div className="bob" style={{ animationDelay: `${bobDelay}s` }}>
          <TieredCritter glyph={data.glyph} star={data.star} size={s(155)} />
        </div>
      </div>

      {/* Bottom-left — HP number over the heart icon baked into the frame */}
      <div
        style={{
          position: 'absolute',
          bottom: pos.hpBottom,
          left: pos.hpLeft,
          width: '18%',
          height: '14%',
          display: 'grid',
          placeItems: 'center',
          fontFamily: 'var(--pixel)',
          fontSize: s(38),
          color: '#fffaee',
          textShadow: outlineShadow,
          pointerEvents: 'none',
        }}
      >
        {data.hp}
      </div>

      {/* Bottom-right — ATK number over the fist icon baked into the frame */}
      <div
        style={{
          position: 'absolute',
          bottom: pos.atkBottom,
          right: pos.atkRight,
          width: '18%',
          height: '14%',
          display: 'grid',
          placeItems: 'center',
          fontFamily: 'var(--pixel)',
          fontSize: s(38),
          color: '#fffaee',
          textShadow: outlineShadow,
          pointerEvents: 'none',
        }}
      >
        {data.atk}
      </div>

      {/* Bottom-middle — animal chip, floats between the heart and fist
          icons baked into the frame. Kept level with the card here
          (the rotated -1deg variant in the codex stays unchanged). */}
      <div
        style={{
          position: 'absolute',
          bottom: pos.chipBottom,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'inline-flex',
          alignItems: 'center',
          gap: s(4),
          padding: `${s(3)}px ${s(9)}px`,
          border: `${Math.max(1, Math.round((2 * width) / 240))}px solid var(--ink)`,
          borderRadius: s(4),
          background: ANIMAL_INFO[data.animal].bg,
          color: 'var(--ink)',
          fontFamily: 'var(--sans)',
          fontWeight: 600,
          fontSize: s(11),
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
        }}
      >
        <span
          style={{
            fontFamily: 'var(--serif)',
            fontSize: s(15),
            color: ANIMAL_INFO[data.animal].accent,
            lineHeight: 1,
          }}
        >
          {ANIMAL_INFO[data.animal].kanji}
        </span>
        <span>{ANIMAL_INFO[data.animal].en}</span>
      </div>
    </div>
  )
}

// === Board-card variant =====================================
// Owned units on the lineup / bench. Two content swaps vs ForgedCard:
//   • banner: name → star indicator (✦ × current star)
//   • bottom chip: animal → fill-counter pip row
// Frame asset is keyed by current star level (★1 / ★2 / ★3) so the
// chrome itself escalates with each combine. Tier numeral + element
// disc kept (battle math still cares).
interface ForgedBoardCardData {
  glyph: GlyphKey
  element: Element
  tier: 1 | 2 | 3 | 4 | 5
  star: 1 | 2 | 3
  /** 1–3, combine-progress pip count. fill=3 && star=3 = capped. */
  fill: 1 | 2 | 3
  hp: number
  atk: number
}

const BOARD_SAMPLES: ForgedBoardCardData[] = [
  // Mix to surface every star × fill combination of interest:
  // ★1 fresh, ★1 with 2 fills (one combine away from ★2), ★2 fresh,
  // ★2 max-fill (one combine from ★3), ★3 capped.
  { glyph: 'tortoise', element: 'earth', tier: 1, star: 1, fill: 1, hp: 7,  atk: 1 },
  { glyph: 'frog',     element: 'fire',  tier: 2, star: 1, fill: 2, hp: 5,  atk: 3 },
  { glyph: 'crane',    element: 'wind',  tier: 1, star: 2, fill: 1, hp: 4,  atk: 4 },
  { glyph: 'koi',      element: 'water', tier: 3, star: 2, fill: 3, hp: 9,  atk: 7 },
  { glyph: 'phoenix',  element: 'fire',  tier: 3, star: 3, fill: 3, hp: 22, atk: 9 },
]

function ForgedBoardCard({
  data,
  width = 240,
  bobDelay = 0,
}: {
  data: ForgedBoardCardData
  width?: number
  bobDelay?: number
}): ReactElement {
  const ROMAN: Record<1 | 2 | 3 | 4 | 5, string> = {
    1: 'I', 2: 'II', 3: 'III', 4: 'IV', 5: 'V',
  }
  const s = (n: number) => Math.round((n * width) / 240)
  const stroke = Math.max(1, Math.round((2 * width) / 240))
  const outlineShadow = [
    `-${stroke}px -${stroke}px 0 #000`,
    ` ${stroke}px -${stroke}px 0 #000`,
    `-${stroke}px  ${stroke}px 0 #000`,
    ` ${stroke}px  ${stroke}px 0 #000`,
    `0 -${stroke}px 0 #000`,
    `0  ${stroke}px 0 #000`,
    `-${stroke}px 0 0 #000`,
    ` ${stroke}px 0 0 #000`,
    `0 ${Math.max(1, stroke + 1)}px ${stroke + 2}px rgba(0,0,0,0.45)`,
  ].join(', ')
  const capped = data.star === 3 && data.fill === 3
  const pos = forgeOverlayPos(data.element)
  return (
    <div
      style={{
        width,
        aspectRatio: '2 / 3',
        backgroundImage: `url(${forgedFrameUrl(data.element, data.star)}), linear-gradient(var(--washi-light), var(--washi-light))`,
        backgroundSize: '100% 100%, 85% 85%',
        backgroundPosition: 'center, center',
        backgroundRepeat: 'no-repeat, no-repeat',
        position: 'relative',
        filter: `drop-shadow(0 ${s(8)}px ${s(14)}px rgba(60, 36, 12, 0.22))`,
      }}
    >
      {/* Top banner — star indicator (✦ × star), replacing the name.
          Ink fill + tight gold halo so the stars read on every frame
          (the painted banner shelf varies in cream/wood saturation
          across ★1/★2/★3); gold alone gets lost on the warmer woods. */}
      <div
        style={{
          position: 'absolute',
          top: pos.nameTop,
          left: '18%',
          right: '22%',
          height: '7%',
          display: 'grid',
          placeItems: 'center',
          fontFamily: 'var(--serif)',
          fontWeight: 700,
          fontSize: s(18),
          color: 'var(--ink)',
          letterSpacing: '0.22em',
          textShadow: [
            `0 0 ${Math.max(1, s(2))}px rgba(245, 200, 90, 0.85)`,
            `0 0 ${Math.max(1, s(4))}px rgba(245, 200, 90, 0.55)`,
            `0 1px 0 rgba(255, 240, 200, 0.7)`,
          ].join(', '),
          pointerEvents: 'none',
        }}
        aria-label={`star ${data.star}`}
      >
        {'✦'.repeat(data.star)}
      </div>

      {/* Top-right tier numeral disc — same as shop-card variant. */}
      <div
        style={{
          position: 'absolute',
          top: pos.tierTop,
          right: pos.tierRight,
          width: '14%',
          height: '10%',
          display: 'grid',
          placeItems: 'center',
          fontFamily: 'var(--serif)',
          fontWeight: 700,
          fontSize: s(16),
          color: 'var(--ink)',
          letterSpacing: '0.02em',
          pointerEvents: 'none',
        }}
      >
        {ROMAN[data.tier]}
      </div>

      {/* Center art — origami glyph, sized to match shop card. */}
      <div
        style={{
          position: 'absolute',
          top: '22%',
          left: '14%',
          right: '14%',
          bottom: '24%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        <div className="bob" style={{ animationDelay: `${bobDelay}s` }}>
          <TieredCritter glyph={data.glyph} star={data.star} size={s(155)} />
        </div>
      </div>

      {/* Bottom-left HP number over the frame's heart icon. */}
      <div
        style={{
          position: 'absolute',
          bottom: pos.hpBottom,
          left: pos.hpLeft,
          width: '18%',
          height: '14%',
          display: 'grid',
          placeItems: 'center',
          fontFamily: 'var(--pixel)',
          fontSize: s(38),
          color: '#fffaee',
          textShadow: outlineShadow,
          pointerEvents: 'none',
        }}
      >
        {data.hp}
      </div>

      {/* Bottom-right ATK number over the frame's fist icon. */}
      <div
        style={{
          position: 'absolute',
          bottom: pos.atkBottom,
          right: pos.atkRight,
          width: '18%',
          height: '14%',
          display: 'grid',
          placeItems: 'center',
          fontFamily: 'var(--pixel)',
          fontSize: s(38),
          color: '#fffaee',
          textShadow: outlineShadow,
          pointerEvents: 'none',
        }}
      >
        {data.atk}
      </div>

      {/* Bottom-middle — fill counter (3 pips). Replaces the animal
          chip from the shop-card variant. fill=N pips filled with
          ink; capped (★3 + fill=3) tints filled pips gold. */}
      <div
        style={{
          position: 'absolute',
          bottom: pos.fillBottom,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'inline-flex',
          alignItems: 'center',
          gap: s(6),
          // Cream pill behind the pips so they stand off the painted
          // frame's wood/gold lower edge. Same washi-light vocabulary
          // as the shop card's animal chip.
          background: 'var(--washi-light)',
          padding: `${s(3)}px ${s(7)}px`,
          border: `${Math.max(1, Math.round((1.5 * width) / 240))}px solid var(--ink)`,
          borderRadius: '999px',
          filter: `drop-shadow(0 ${Math.max(1, s(1))}px 0 rgba(0,0,0,0.25))`,
          pointerEvents: 'none',
        }}
        aria-label={capped ? 'capped' : `${data.fill} of 3`}
      >
        {[1, 2, 3].map((i) => {
          const filled = i <= data.fill
          const dot = s(14)
          const border = Math.max(1, Math.round((2 * width) / 240))
          return (
            <span
              key={i}
              style={{
                width: dot,
                height: dot,
                borderRadius: '50%',
                border: `${border}px solid var(--ink)`,
                background: filled
                  ? capped
                    ? 'var(--gold-deep)'
                    : 'var(--ink)'
                  : 'transparent',
                filter: `drop-shadow(0 1px 0 rgba(0,0,0,0.25))`,
              }}
            />
          )
        })}
      </div>
    </div>
  )
}
